import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Draw as DrawIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  PictureAsPdf as PdfIcon,
  Mail as MailIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { ZulassungsantragService } from '../services/zulassungsantragService';
import { FahrzeugService } from '../services/fahrzeugService';
import { HalterService } from '../services/halterService';
import { DokumentService } from '../services/dokumentService';
import { SabrinaService } from '../services/sabrinaService';
import { SabrinaEmpfangeneNachricht } from '../types/sabrina';
import { VersicherungService, Versicherung } from '../services/versicherungService';
import { apiClient } from '../services/api';
import { 
  ZulassungsantragResponse, 
  getGeschaeftsvorfallTypLabel, 
  getComponentVisibility, 
  getFieldVisibility, 
  ComponentVisibility, 
  FieldVisibility,
  StatusAbfrageResult,
  SubmitResult,
  GksQuittungResult
} from '../types/antrag';
import {
  Fahrzeug,
  Kennzeichen,
  KennzeichenTyp,
  KennzeichenCreateRequest,
  Verwertungsnachweis
} from '../types/fahrzeug';
import { Halter, PersonTyp, Geschlecht } from '../types/halter';
import {
  GrunddatenCard,
  FahrzeugdatenCard,
  KennzeichendatenCard,
  HalterdatenCard,
  VerwertungsnachweisCard,
  AblehnungsgrundCard
} from '../components/antrag-details';
import RequiredDataChecklist from '../components/antrag-details/RequiredDataChecklist';

const parseKennzeichen = (text: string): { bezirk: string; buchstaben: string; nummer: string } | null => {
  const normalized = text.trim().toUpperCase().replace(/-/g, ' ').replace(/\s+/g, ' ');
  const parts = normalized.split(' ').filter(Boolean);

  if (parts.length === 3) {
    const [b, bu, n] = parts;
    if (/^[A-ZÄÖÜ]{1,3}$/.test(b) && /^[A-ZÄÖÜ]{1,3}$/.test(bu) && /^\d{1,4}$/.test(n)) {
      return { bezirk: b, buchstaben: bu, nummer: n };
    }
  }

  if (parts.length === 2) {
    if (/^[A-ZÄÖÜ]{1,3}$/.test(parts[0])) {
      const m = parts[1].match(/^([A-ZÄÖÜ]{1,3})(\d{1,4})$/);
      if (m) return { bezirk: parts[0], buchstaben: m[1], nummer: m[2] };
    }
    if (/^\d{1,4}$/.test(parts[1])) {
      const m = parts[0].match(/^([A-ZÄÖÜ]{1,3})([A-ZÄÖÜ]{1,3})$/);
      if (m) return { bezirk: m[1], buchstaben: m[2], nummer: parts[1] };
    }
  }

  const noSpace = normalized.replace(/\s/g, '');
  const m = noSpace.match(/^([A-ZÄÖÜ]{1,3})([A-ZÄÖÜ]{1,3})(\d{1,4})$/);
  if (m) return { bezirk: m[1], buchstaben: m[2], nummer: m[3] };

  return null;
};

const ZulassungsantragDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [antrag, setAntrag] = useState<ZulassungsantragResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<any>({});
  const [saving, setSaving] = useState(false);
  
  // Document states
  const [vollmachtExists, setVollmachtExists] = useState<boolean>(false);
  const [sepaExists, setSepaExists] = useState<boolean>(false);
  const [loadingDocuments, setLoadingDocuments] = useState<boolean>(false);
  
  // Versicherung state
  const [versicherung, setVersicherung] = useState<Versicherung | null>(null);
  const [loadingVersicherung, setLoadingVersicherung] = useState<boolean>(false);
  
  // Verwertungsnachweis state (für Außerbetriebsetzung)
  const [verwertungsnachweis, setVerwertungsnachweis] = useState<Verwertungsnachweis | null>(null);
  const [loadingVerwertungsnachweis, setLoadingVerwertungsnachweis] = useState<boolean>(false);
  const [showAllCountries, setShowAllCountries] = useState<boolean>(false);
  
  // SABRINA empfangene Nachrichten
  const [sabrinaNachrichten, setSabrinaNachrichten] = useState<SabrinaEmpfangeneNachricht[]>([]);
  const [sabrinaLoading, setSabrinaLoading] = useState<boolean>(false);

  // Automatischer Status-Workflow Polling (rein im Hintergrund, ohne eigene UI-Anzeige).
  // Backend prüft SABRINA nur noch alle ~60s (kein KBA-Status-Polling mehr im
  // Hintergrund); ein Antrag kann daher sehr lange in GESENDET/IN_BEARBEITUNG/GEPRUEFT
  // bleiben. Deshalb Backoff statt festem 5s-Intervall, damit der Tab nicht dauerhaft
  // im 5s-Rhythmus pollt, wenn nichts Neues zu erwarten ist.
  const STATUS_POLLING_DELAYS_MS = [5000, 5000, 10000, 15000, 30000, 60000];
  const statusPollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const statusPollingAttemptRef = useRef(0);
  const isMountedRef = useRef(true);

  // KBA Submit & Status states
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitResult, setSubmitResult] = useState<SubmitResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [queryingStatus, setQueryingStatus] = useState<boolean>(false);
  const [statusResult, setStatusResult] = useState<StatusAbfrageResult | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Component visibility based on geschaeftsvorfallTyp
  const componentVisibility: ComponentVisibility = antrag 
    ? getComponentVisibility(antrag.geschaeftsvorfallTyp)
    : getComponentVisibility('NEUZULASSUNG'); // Default

  // Field visibility based on geschaeftsvorfallTyp AND antragstellerTyp
  const fieldVisibility: FieldVisibility = antrag 
    ? getFieldVisibility(antrag.geschaeftsvorfallTyp, antrag.antragstellerTyp)
    : getFieldVisibility('NEUZULASSUNG', 'EIGENE_PERSON'); // Default

  useEffect(() => {
    if (id) {
      loadAntrag(parseInt(id, 10));
    }
  }, [id]);

  const loadAntrag = async (antragId: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await ZulassungsantragService.getZulassungsantragById(antragId);
      setAntrag(response);
      // Load document status, versicherung and SABRINA after loading antrag
      await Promise.all([
        loadDocumentStatus(antragId),
        loadVersicherung(antragId),
        loadSabrinaNachrichten(antragId)
      ]);
      // Starte automatisches Polling bei relevanten Status
      startStatusPolling(response.status, response.id);
      
      // Load Verwertungsnachweis for Außerbetriebsetzung if Fahrzeug exists
      if (response.geschaeftsvorfallTyp === 'AUSSERBETRIEBSETZUNG' && response.fahrzeug?.id) {
        await loadVerwertungsnachweis(response.fahrzeug.id);
      }
    } catch (err) {
      console.error('Fehler beim Laden des Antrags:', err);
      setError('Fehler beim Laden des Antrags');
    } finally {
      setLoading(false);
    }
  };

  // Automatisches Status-Polling starten/stoppen
  const startStatusPolling = (status: string, antragId: number) => {
    // Stoppe vorheriges Polling
    stopStatusPolling();

    // Nur bei automatischen Workflow-Status pollen
    const pollingStatuses = ['GESENDET', 'IN_BEARBEITUNG', 'GEPRUEFT'];
    if (!pollingStatuses.includes(status.toUpperCase())) {
      return;
    }

    statusPollingAttemptRef.current = 0;
    scheduleNextStatusPoll(antragId);
  };

  // Pollt einmal und plant den nächsten Versuch mit wachsendem Intervall (Backoff).
  // Das Backend prüft SABRINA nur noch alle ~60s und führt kein automatisches
  // KBA-Status-Polling mehr durch, daher lohnt sich ein festes 5s-Intervall nicht mehr.
  const scheduleNextStatusPoll = (antragId: number) => {
    const attempt = statusPollingAttemptRef.current;
    const delay = STATUS_POLLING_DELAYS_MS[Math.min(attempt, STATUS_POLLING_DELAYS_MS.length - 1)];
    statusPollingAttemptRef.current = attempt + 1;

    statusPollingTimeoutRef.current = setTimeout(async () => {
      if (!isMountedRef.current) {
        stopStatusPolling();
        return;
      }

      try {
        const refreshed = await ZulassungsantragService.getZulassungsantragById(antragId);

        if (!isMountedRef.current) return;

        setAntrag(refreshed);

        // Stoppe Polling wenn End-Status erreicht
        const endStatuses = ['ABGESCHLOSSEN', 'FEHLER', 'ABGELEHNT', 'ERSTELLT'];
        if (endStatuses.includes(refreshed.status.toUpperCase())) {
          stopStatusPolling();
          // Lade SABRINA-Nachrichten neu wenn abgeschlossen
          if (refreshed.status.toUpperCase() === 'ABGESCHLOSSEN') {
            await loadSabrinaNachrichten(antragId);
          }
          return;
        }
      } catch (err) {
        console.error('Fehler beim Status-Polling:', err);
      }

      if (isMountedRef.current) {
        scheduleNextStatusPoll(antragId);
      }
    }, delay);
  };

  const stopStatusPolling = () => {
    if (statusPollingTimeoutRef.current) {
      clearTimeout(statusPollingTimeoutRef.current);
      statusPollingTimeoutRef.current = null;
    }
  };

  // Cleanup beim Unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopStatusPolling();
    };
  }, []);

  const loadSabrinaNachrichten = async (antragId: number) => {
    try {
      setSabrinaLoading(true);
      const nachrichten = await SabrinaService.getEmpfangeneNachrichtenByAntragId(antragId);
      setSabrinaNachrichten(nachrichten);
    } catch (err) {
      console.error('Fehler beim Laden der SABRINA-Nachrichten:', err);
    } finally {
      setSabrinaLoading(false);
    }
  };

  const handleDownloadSabrinaDokument = async (dokumentId: number) => {
    try {
      await SabrinaService.downloadDokument(dokumentId);
    } catch (err: any) {
      console.error('Fehler beim Herunterladen des SABRINA-Dokuments:', err);
      setError(err.message || 'Fehler beim Herunterladen des Dokuments');
    }
  };

  const loadDocumentStatus = async (antragId: number) => {
    try {
      const status = await DokumentService.getDokumenteStatus(antragId);
      setVollmachtExists(status.vollmachtExists);
      setSepaExists(status.sepaExists);
    } catch (err) {
      console.error('Fehler beim Laden des Dokumentenstatus:', err);
    }
  };

  const loadVersicherung = async (antragId: number) => {
    try {
      setLoadingVersicherung(true);
      const vers = await VersicherungService.getVersicherungByZulassungsantragId(antragId);
      setVersicherung(vers);
    } catch (err) {
      console.error('Fehler beim Laden der Versicherung:', err);
      setVersicherung(null);
    } finally {
      setLoadingVersicherung(false);
    }
  };

  const loadVerwertungsnachweis = async (fahrzeugId: number) => {
    try {
      setLoadingVerwertungsnachweis(true);
      const nachweis = await FahrzeugService.getVerwertungsnachweis(fahrzeugId);
      setVerwertungsnachweis(nachweis);
    } catch (err) {
      console.error('Fehler beim Laden des Verwertungsnachweises:', err);
      setVerwertungsnachweis(null);
    } finally {
      setLoadingVerwertungsnachweis(false);
    }
  };

  const handleGenerateDocument = async (type: 'vollmacht' | 'sepa') => {
    if (!antrag) return;
    
    try {
      setLoadingDocuments(true);
      
      if (type === 'vollmacht') {
        await DokumentService.createVollmacht(antrag.id);
        setVollmachtExists(true);
        setError(null);
      } else {
        // For SEPA, we can optionally pass an IBAN
        await DokumentService.createSepaMandat(antrag.id);
        setSepaExists(true);
        setError(null);
      }
      
      console.log(`${type === 'vollmacht' ? 'Vollmacht' : 'SEPA-Mandat'} erfolgreich generiert`);
    } catch (err: any) {
      console.error(`Fehler beim Generieren des ${type === 'vollmacht' ? 'Vollmacht' : 'SEPA-Mandats'}:`, err);
      setError(err.response?.data?.message || `Fehler beim Generieren des ${type === 'vollmacht' ? 'Vollmacht' : 'SEPA-Mandats'}`);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleOpenDocument = async (type: 'vollmacht' | 'sepa') => {
    if (!antrag) return;
    
    try {
      setLoadingDocuments(true);
      
      let blob: Blob;
      if (type === 'vollmacht') {
        blob = await DokumentService.downloadVollmacht(antrag.id);
      } else {
        blob = await DokumentService.downloadSepa(antrag.id);
      }
      
      // Create a URL for the blob and open it in a new tab
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Clean up the URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
      
    } catch (err: any) {
      console.error(`Fehler beim Öffnen des ${type === 'vollmacht' ? 'Vollmacht' : 'SEPA-Mandats'}:`, err);
      setError(err.response?.data?.message || `Fehler beim Öffnen des ${type === 'vollmacht' ? 'Vollmacht' : 'SEPA-Mandats'}`);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDeleteDocument = async (type: 'vollmacht' | 'sepa') => {
    if (!antrag) return;
    
    const confirmed = window.confirm(
      `Möchten Sie ${type === 'vollmacht' ? 'die Vollmacht' : 'das SEPA-Mandat'} wirklich löschen?`
    );
    
    if (!confirmed) return;
    
    try {
      setLoadingDocuments(true);
      
      if (type === 'vollmacht') {
        await DokumentService.deleteVollmacht(antrag.id);
        setVollmachtExists(false);
      } else {
        await DokumentService.deleteSepaMandat(antrag.id);
        setSepaExists(false);
      }
      
      setError(null);
      console.log(`${type === 'vollmacht' ? 'Vollmacht' : 'SEPA-Mandat'} erfolgreich gelöscht`);
    } catch (err: any) {
      console.error(`Fehler beim Löschen des ${type === 'vollmacht' ? 'Vollmacht' : 'SEPA-Mandats'}:`, err);
      setError(err.response?.data?.message || `Fehler beim Löschen des ${type === 'vollmacht' ? 'Vollmacht' : 'SEPA-Mandats'}`);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleEdit = () => {
    navigate(`/antragstellung?edit=${antrag?.id}`);
  };

  // QES Unterschreiben
  const handleQesSignieren = async () => {
    if (!antrag) return;
    
    const confirmed = window.confirm(
      'Möchten Sie diesen Antrag wirklich mit QES unterschreiben?\n\n' +
      'Der Antrag wird qualifiziert elektronisch signiert.'
    );
    
    if (!confirmed) return;
    
    try {
      setSubmitting(true);
      setSubmitError(null);
      setSubmitResult(null);
      setSuccessMessage(null);
      
      await ZulassungsantragService.signZulassungsantrag(antrag.id);
      
      await loadAntrag(antrag.id);
      setSuccessMessage('Antrag erfolgreich mit QES unterschrieben!');
      
    } catch (err: any) {
      console.error('Fehler beim QES Unterschreiben:', err);
      setSubmitError(err.response?.data?.message || err.message || 'Fehler beim QES Unterschreiben');
    } finally {
      setSubmitting(false);
    }
  };

  // Antrag an KBA senden
  const handleSubmitToKba = async () => {
    if (!antrag) return;
    
    const confirmed = window.confirm(
      'Möchten Sie diesen Antrag wirklich an das KBA senden?\n\n' +
      'Der Antrag wird zur Prüfung übermittelt und kann danach nicht mehr bearbeitet werden.'
    );
    
    if (!confirmed) return;
    
    try {
      setSubmitting(true);
      setSubmitError(null);
      setSubmitResult(null);
      setSuccessMessage(null);
      
      const result = await ZulassungsantragService.submitZulassungsantrag(antrag.id);
      setSubmitResult(result);
      
      // Antrag neu laden um aktualisierten Status zu sehen
      await loadAntrag(antrag.id);
      
      if (result.quittung?.success) {
        setSuccessMessage('Antrag erfolgreich an das KBA gesendet!');
      } else if (result.quittung?.fehlerMeldungen && result.quittung.fehlerMeldungen.length > 0) {
        setSubmitError(`KBA-Fehler: ${result.quittung.fehlerMeldungen.join(', ')}`);
      }
      
    } catch (err: any) {
      console.error('Fehler beim Senden an KBA:', err);
      setSubmitError(err.response?.data?.message || err.message || 'Fehler beim Senden an das KBA');
    } finally {
      setSubmitting(false);
    }
  };

  // Workflow neu starten (bei Status FEHLER)
  const handleRestartWorkflow = async () => {
    if (!antrag) return;
    
    const confirmed = window.confirm(
      'Möchten Sie den Antrag zurücksetzen?\n\n' +
      'Der Antrag wird auf "wartet auf KBA-Bescheid" zurückgesetzt und beim nächsten ' +
      'automatischen SABRINA-Abgleich erneut berücksichtigt (kein erneuter Versand an das KBA).'
    );
    
    if (!confirmed) return;
    
    try {
      setSubmitting(true);
      setSubmitError(null);
      setSuccessMessage(null);
      
      const result = await ZulassungsantragService.restartWorkflow(antrag.id);
      setSuccessMessage(result.message);
      
      // Antrag neu laden (startet automatisches Status-Polling)
      await loadAntrag(antrag.id);
      
    } catch (err: any) {
      console.error('Fehler beim Neustarten des Workflows:', err);
      setSubmitError(err.response?.data?.message || err.message || 'Fehler beim Neustarten des Workflows');
    } finally {
      setSubmitting(false);
    }
  };
  const handleQueryKbaStatus = async () => {
    if (!antrag) return;
    
    try {
      setQueryingStatus(true);
      setStatusError(null);
      setStatusResult(null);
      setSuccessMessage(null);
      
      const result = await ZulassungsantragService.queryKbaStatus(antrag.id);
      setStatusResult(result);
      
      // Antrag neu laden um aktualisierten Status zu sehen
      await loadAntrag(antrag.id);
      
      // SABRINA-Nachrichten neu laden wenn Status ABGESCHLOSSEN
      if (result.neuerStatus?.toUpperCase() === 'ABGESCHLOSSEN') {
        await loadSabrinaNachrichten(antrag.id);
      }
      
      if (result.neuerStatus) {
        setSuccessMessage(`Status aktualisiert: ${result.neuerStatus}`);
      }
      
    } catch (err: any) {
      console.error('Fehler bei KBA-Statusabfrage:', err);
      setStatusError(err.response?.data?.message || err.message || 'Fehler bei der Statusabfrage');
    } finally {
      setQueryingStatus(false);
    }
  };

  const handleOpenDialog = async (section: string) => {
    if (!antrag) return;
    
    let initialData = {};
    switch (section) {
      case 'grunddaten':
        initialData = {
          status: antrag.status,
          geschaeftsvorfallTyp: antrag.geschaeftsvorfallTyp
        };
        break;
      case 'fahrzeugdaten':
        // Use embedded fahrzeug data from antrag
        if (antrag.fahrzeug) {
          initialData = {
            fin: antrag.fahrzeug.fin || '',
            hersteller: antrag.fahrzeug.hersteller || '',
            modell: antrag.fahrzeug.modell || '',
            saisonVon: antrag.fahrzeug.saisonVon,
            saisonBis: antrag.fahrzeug.saisonBis,
            kennzeichenTyp: antrag.fahrzeug.kennzeichenTyp,
            hsn: antrag.fahrzeug.hsn || '',
            tsn: antrag.fahrzeug.tsn || '',
            marke: antrag.fahrzeug.marke || '',
            verwendungszweck: antrag.fahrzeug.verwendungszweck || '',
            zb1Nummer: antrag.fahrzeug.zb1Nummer || '',
            zb2Nummer: antrag.fahrzeug.zb2Nummer || '',
            zb1Sicherheitscode: antrag.fahrzeug.zb1Sicherheitscode || '',
            zb2Sicherheitscode: antrag.fahrzeug.zb2Sicherheitscode || '',
            kraftstoffart: antrag.fahrzeug.kraftstoffart || '',
            elektrofahrzeug: antrag.fahrzeug.kraftstoffart?.toLowerCase() === 'elektro',
            // eVB-Nummer aus Versicherung
            evbNummer: versicherung?.evbNummer || ''
          };
        } else {
          // Fallback if no fahrzeug data available
          initialData = {
            fin: antrag.fin || '',
            hersteller: '',
            modell: '',
            saisonVon: null,
            saisonBis: null,
            kennzeichenTyp: null,
            hsn: '',
            tsn: '',
            marke: '',
            verwendungszweck: '',
            zb1Nummer: '',
            zb2Nummer: '',
            zb1Sicherheitscode: '',
            zb2Sicherheitscode: '',
            // eVB-Nummer aus Versicherung
            evbNummer: versicherung?.evbNummer || ''
          };
        }
        break;
      case 'kennzeichendaten':
        const isAusserbetriebsetzung = antrag.geschaeftsvorfallTyp === 'AUSSERBETRIEBSETZUNG';
        const isWiederzulassung = antrag.geschaeftsvorfallTyp === 'WIEDERZULASSUNG' || antrag.geschaeftsvorfallTyp === 'WIEDERZULASSUNG_SELBER_HALTER';
        const isUmschreibung =
          antrag.geschaeftsvorfallTyp === 'UMSCHREIBUNG_ANDERER_BEZIRK_OHNE_HALTERWECHSEL' ||
          antrag.geschaeftsvorfallTyp === 'UMSCHREIBUNG_ANDERER_BEZIRK_MIT_HALTERWECHSEL' ||
          antrag.geschaeftsvorfallTyp === 'UMSCHREIBUNG_INNERHALB_MIT_HALTERWECHSEL';
        // Außerbetriebsetzung, Wiederzulassung und Umschreibung benötigen beide Siegel-Sicherheitscodes (vorn/hinten)
        const needsSiegelCodes = isAusserbetriebsetzung || isWiederzulassung || isUmschreibung;
        if (antrag.fahrzeug) {
          if (needsSiegelCodes) {
            // For AUSSERBETRIEBSETZUNG and WIEDERZULASSUNG: one old license plate with two seal codes (front and back)
            // Kennzeichendaten kommen aus altesKennzeichenVorn (identisch mit Hinten außer Siegel-Code)
            const altesKennzeichen = antrag.fahrzeug.altesKennzeichenVorn;
            initialData = {
              hasNewKennzeichen: !!antrag.fahrzeug.neuesKennzeichen,
              newKennzeichenBezirk: antrag.fahrzeug.neuesKennzeichen?.bezirk || '',
              newKennzeichenBuchstaben: antrag.fahrzeug.neuesKennzeichen?.buchstaben || '',
              newKennzeichenNummer: antrag.fahrzeug.neuesKennzeichen?.nummer || '',
              newKennzeichenSonderkennzeichen: antrag.fahrzeug.neuesKennzeichen?.sonderkennzeichen || '',
              newKennzeichenSaisonVon: antrag.fahrzeug.neuesKennzeichen?.saisonVon || '',
              newKennzeichenSaisonBis: antrag.fahrzeug.neuesKennzeichen?.saisonBis || '',
              newKennzeichenReserviert: antrag.fahrzeug.neuesKennzeichen?.reserviert || false,
              newKennzeichenReservierungPin: antrag.fahrzeug.neuesKennzeichen?.reservierungspin || '',
              // Gemeinsame Kennzeichendaten (einmal eingeben) – Fallback auf Hinten falls nur Hinten vorhanden
              hasOldKennzeichen: !!(antrag.fahrzeug.altesKennzeichenVorn || antrag.fahrzeug.altesKennzeichenHinten),
              oldKennzeichenBezirk: (antrag.fahrzeug.altesKennzeichenVorn || antrag.fahrzeug.altesKennzeichenHinten)?.bezirk || '',
              oldKennzeichenBuchstaben: (antrag.fahrzeug.altesKennzeichenVorn || antrag.fahrzeug.altesKennzeichenHinten)?.buchstaben || '',
              oldKennzeichenNummer: (antrag.fahrzeug.altesKennzeichenVorn || antrag.fahrzeug.altesKennzeichenHinten)?.nummer || '',
              oldKennzeichenSonderkennzeichen: (antrag.fahrzeug.altesKennzeichenVorn || antrag.fahrzeug.altesKennzeichenHinten)?.sonderkennzeichen || '',
              // Vorn/Hinten-Flags und Siegel-Codes separat aus DB-Zustand laden
              hasOldKennzeichenVorn: !!antrag.fahrzeug.altesKennzeichenVorn,
              oldKennzeichenVornBezirk: antrag.fahrzeug.altesKennzeichenVorn?.bezirk || '',
              oldKennzeichenVornBuchstaben: antrag.fahrzeug.altesKennzeichenVorn?.buchstaben || '',
              oldKennzeichenVornNummer: antrag.fahrzeug.altesKennzeichenVorn?.nummer || '',
              oldKennzeichenVornSonderkennzeichen: antrag.fahrzeug.altesKennzeichenVorn?.sonderkennzeichen || '',
              oldKennzeichenVornSiegelSicherheitscode: antrag.fahrzeug.altesKennzeichenVorn?.siegelSicherheitscode || '',
              hasOldKennzeichenHinten: !!antrag.fahrzeug.altesKennzeichenHinten,
              oldKennzeichenHintenBezirk: antrag.fahrzeug.altesKennzeichenHinten?.bezirk || '',
              oldKennzeichenHintenBuchstaben: antrag.fahrzeug.altesKennzeichenHinten?.buchstaben || '',
              oldKennzeichenHintenNummer: antrag.fahrzeug.altesKennzeichenHinten?.nummer || '',
              oldKennzeichenHintenSonderkennzeichen: antrag.fahrzeug.altesKennzeichenHinten?.sonderkennzeichen || '',
              oldKennzeichenHintenSiegelSicherheitscode: antrag.fahrzeug.altesKennzeichenHinten?.siegelSicherheitscode || '',
              // Fahrzeugbezogene Kennzeichenreservierung (nur bei Außerbetriebsetzung)
              kennzeichenReservierung: antrag.kennzeichenReservierung || false
            };
          } else {
            // For other business processes: single old license plate (without position and siegelSicherheitscode)
            // Note: WIEDERZULASSUNG is handled above with needsSiegelCodes
            initialData = {
              hasNewKennzeichen: !!antrag.fahrzeug.neuesKennzeichen,
              newKennzeichenBezirk: antrag.fahrzeug.neuesKennzeichen?.bezirk || '',
              newKennzeichenBuchstaben: antrag.fahrzeug.neuesKennzeichen?.buchstaben || '',
              newKennzeichenNummer: antrag.fahrzeug.neuesKennzeichen?.nummer || '',
              newKennzeichenSonderkennzeichen: antrag.fahrzeug.neuesKennzeichen?.sonderkennzeichen || '',
              newKennzeichenSaisonVon: antrag.fahrzeug.neuesKennzeichen?.saisonVon || '',
              newKennzeichenSaisonBis: antrag.fahrzeug.neuesKennzeichen?.saisonBis || '',
              newKennzeichenReserviert: antrag.fahrzeug.neuesKennzeichen?.reserviert || false,
              newKennzeichenReservierungPin: antrag.fahrzeug.neuesKennzeichen?.reservierungspin || '',
              hasOldKennzeichen: !!antrag.fahrzeug.altesKennzeichen,
              oldKennzeichenBezirk: antrag.fahrzeug.altesKennzeichen?.bezirk || '',
              oldKennzeichenBuchstaben: antrag.fahrzeug.altesKennzeichen?.buchstaben || '',
              oldKennzeichenNummer: antrag.fahrzeug.altesKennzeichen?.nummer || '',
              oldKennzeichenSonderkennzeichen: antrag.fahrzeug.altesKennzeichen?.sonderkennzeichen || ''
            };
          }
        } else {
          initialData = {
            hasNewKennzeichen: false,
            newKennzeichenBezirk: '',
            newKennzeichenBuchstaben: '',
            newKennzeichenNummer: '',
            newKennzeichenSonderkennzeichen: '',
            newKennzeichenSaisonVon: '',
            newKennzeichenSaisonBis: '',
            newKennzeichenReserviert: false,
            newKennzeichenReservierungPin: '',
            hasOldKennzeichen: false,
            oldKennzeichenBezirk: '',
            oldKennzeichenBuchstaben: '',
            oldKennzeichenNummer: '',
            oldKennzeichenSonderkennzeichen: ''
          };
        }
        break;
      case 'versandadresse':
        initialData = {
          versandName: antrag.versandName || '',
          versandVorname: antrag.versandVorname || '',
          versandStrasse: antrag.versandStrasse || '',
          versandHausnummer: antrag.versandHausnummer || '',
          versandPostleitzahl: antrag.versandPostleitzahl || '',
          versandOrt: antrag.versandOrt || ''
        };
        break;
      case 'halterdaten':
        // Use embedded halter data from antrag
        if (antrag.halter) {
          initialData = {
            personTyp: antrag.halter.personTyp || '',
            vorname: antrag.halter.vorname || '',
            nachname: antrag.halter.nachname || '',
            firma: antrag.halter.firma || '',
            geburtsdatum: antrag.halter.geburtsdatum || '',
            geburtsort: antrag.halter.geburtsort || '',
            geschlecht: antrag.halter.geschlecht || '',
            strasse: antrag.halter.strasse || '',
            hausnummer: antrag.halter.hausnummer || '',
            adresszusatz: antrag.halter.adresszusatz || '',
            plz: antrag.halter.plz || '',
            ort: antrag.halter.ort || '',
            land: 'D',
            telefon: antrag.halter.telefon || '',
            iban: antrag.halter.iban || '',
            email: antrag.halter.email || ''
          };
        } else {
          // Fallback if no halter data available
          initialData = {
            personTyp: '',
            vorname: antrag.vorname || '',
            nachname: antrag.nachname || '',
            firma: '',
            geburtsdatum: '',
            geburtsort: '',
            strasse: '',
            hausnummer: '',
            adresszusatz: '',
            plz: '',
            ort: '',
            land: 'D',
            telefon: antrag.telefonnummer || '',
            iban: '',
            email: antrag.email || ''
          };
        }
        break;
      case 'verwertungsnachweis':
        // Verwertungsnachweis-Daten aus State laden, Default für staatVerwertung ist 'D' (Deutschland)
        initialData = {
          betriebsnummerDemontagebetrieb: verwertungsnachweis?.betriebsnummerDemontagebetrieb || '',
          datumAusstellung: verwertungsnachweis?.datumAusstellung || '',
          merkmalVerwertungsnachweis: verwertungsnachweis?.merkmalVerwertungsnachweis || '',
          staatVerwertung: verwertungsnachweis?.staatVerwertung || 'D'
        };
        // Reset showAllCountries when opening dialog
        setShowAllCountries(false);
        break;
    }
    
    setEditFormData(initialData);
    setOpenDialog(section);
  };

  const handleCloseDialog = () => {
    setOpenDialog(null);
    setEditFormData({});
    setShowAllCountries(false);
  };

  const handleSaveChanges = async () => {
    if (!antrag || !openDialog) return;
    
    // Whitespace-Bereinigung aller Textfelder vor dem Speichern
    const fd = Object.fromEntries(
      Object.entries(editFormData).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
    ) as typeof editFormData;
    
    try {
      setSaving(true);
      
      let updatedData;
      
      switch (openDialog) {
        case 'grunddaten':
          // Update the main Zulassungsantrag with status and geschaeftsvorfallTyp
          // Create a minimal form data structure with only available fields
          const grunddatenUpdate = {
            geschaeftsvorfallTyp: fd.geschaeftsvorfallTyp,
            halterTyp: antrag.halterTyp,
            vorname: antrag.vorname,
            nachname: antrag.nachname,
            geburtstag: antrag.geburtstag,
            geburtsort: antrag.geburtsort,
            email: antrag.email,
            telefonnummer: antrag.telefonnummer,
            fahrzeugTyp: antrag.fahrzeugTyp,
            fin: antrag.fin,
            zbIINummer: antrag.zbIINummer,
            zbIISicherheitscode: antrag.zbIISicherheitscode,
            briefnummer: '',
            zbIISicherheitscodeZB2: '',
            evb: '',
            alteKennzeichenStadt: '',
            alteKennzeichenBuchstaben: '',
            alteKennzeichenNummern: '',
            neueKennzeichenStadt: '',
            neueKennzeichenBuchstaben: '',
            neueKennzeichenNummern: '',
            sonderkennzeichen: '',
            reservierungsPin: antrag.reservierungsPin,
            saisonkennzeichen: false,
            saisonVon: '',
            saisonBis: '',
            zulassungsstelle: antrag.zulassungsstelle,
            datumZulassung: '',
            iban: antrag.halter.iban,
            bankName: antrag.bankName,
            bankVorname: antrag.bankVorname,
            gkIdZulB: '',
            kennzeichen: '',
            kennzeichenart: antrag.kennzeichenart || '',
            reservierungsnummer: '',
            elektrokennzeichen: false,
            feinstaubplakette: false,
            vbNummer: '',
            verwendungszweck: '',
            berufsschluessel: '',
            huMonatJahr: '',
            huPruefziffer: '',
            huUeberwachungsorganisation: '',
            versandVorname: antrag.versandVorname,
            versandName: antrag.versandName,
            versandStrasse: antrag.versandStrasse,
            versandHausnummer: antrag.versandHausnummer,
            versandAdressierungszusaetze: antrag.versandAdressierungszusaetze,
            versandPostleitzahl: antrag.versandPostleitzahl,
            versandOrt: antrag.versandOrt,
            kundeId: antrag.kunde.id,
            status: fd.status
          };
          updatedData = await ZulassungsantragService.updateZulassungsantrag(antrag.id, grunddatenUpdate);
          setAntrag(updatedData);
          break;
          
        case 'fahrzeugdaten':
          // Update or Create Fahrzeug object via specific API
          try {
            let currentFahrzeug: Fahrzeug | null | undefined = antrag.fahrzeug;

            // Versuche Fahrzeug zu laden, falls nicht im Antrag eingebettet
            if (!currentFahrzeug || !currentFahrzeug.id) {
              try {
                currentFahrzeug = await FahrzeugService.getFahrzeugByZulassungsantragId(antrag.id);
              } catch (err) {
                console.log('Fahrzeug existiert noch nicht, wird erstellt...');
                currentFahrzeug = null;
              }
            }

            // Fahrzeug-Payload mit Kennzeichen-Daten (falls vorhanden)
            const fahrzeugPayload = {
              fin: fd.fin,
              hersteller: fd.hersteller,
              modell: fd.modell,
              saisonVon: fd.saisonVon,
              saisonBis: fd.saisonBis,
              kennzeichenTyp: fd.kennzeichenTyp,
              hsn: fd.hsn,
              tsn: fd.tsn,
              marke: fd.marke,
              verwendungszweck: fd.verwendungszweck,
              zb1Nummer: fd.zb1Nummer,
              zb2Nummer: fd.zb2Nummer,
              zb1Sicherheitscode: fd.zb1Sicherheitscode,
              zb2Sicherheitscode: fd.zb2Sicherheitscode,
              kraftstoffart: fd.elektrofahrzeug ? 'Elektro' : (fd.kraftstoffart?.toLowerCase() === 'elektro' ? null : fd.kraftstoffart || null),
              // Bestehende Kennzeichen beibehalten (alle Typen)
              altesKennzeichen: currentFahrzeug?.altesKennzeichen || null,
              altesKennzeichenVorn: currentFahrzeug?.altesKennzeichenVorn || null,
              altesKennzeichenHinten: currentFahrzeug?.altesKennzeichenHinten || null,
              neuesKennzeichen: currentFahrzeug?.neuesKennzeichen || null
            };

            let fahrzeugId: number;

            if (!currentFahrzeug || !currentFahrzeug.id) {
              // Fahrzeug existiert noch nicht -> Erstellen
              console.log('Erstelle neues Fahrzeug...');
              const newFahrzeug = await FahrzeugService.createFahrzeug(fahrzeugPayload);
              fahrzeugId = newFahrzeug.id!;
              
              // Fahrzeug mit Zulassungsantrag verknüpfen
              console.log('Verknüpfe Fahrzeug mit Zulassungsantrag...');
              await apiClient.put(`/zulassungsantraege/${antrag.id}/fahrzeug/${fahrzeugId}`);
              console.log('Fahrzeug erfolgreich erstellt und verknüpft');
            } else {
              // Fahrzeug existiert bereits -> Aktualisieren
              fahrzeugId = currentFahrzeug.id;
              await FahrzeugService.updateFahrzeug(fahrzeugId, fahrzeugPayload);
              console.log('Fahrzeugdaten erfolgreich aktualisiert');
            }

            // eVB-Nummer speichern (Versicherung)
            if (fd.evbNummer) {
              try {
                if (versicherung && versicherung.id) {
                  // Versicherung aktualisieren
                  await VersicherungService.updateVersicherung(versicherung.id, {
                    zulassungsantragId: antrag.id,
                    evbNummer: fd.evbNummer
                  });
                  console.log('Versicherung erfolgreich aktualisiert');
                } else {
                  // Neue Versicherung erstellen
                  const newVersicherung = await VersicherungService.createVersicherung({
                    zulassungsantragId: antrag.id,
                    evbNummer: fd.evbNummer
                  });
                  setVersicherung(newVersicherung);
                  console.log('Versicherung erfolgreich erstellt');
                }
              } catch (versErr) {
                console.error('Fehler beim Speichern der Versicherung:', versErr);
                // Nicht abbrechen, nur warnen
              }
            }

            // Reload the full antrag to get updated data
            const refreshedAntrag = await ZulassungsantragService.getZulassungsantragById(antrag.id);
            setAntrag(refreshedAntrag);
            // Reload versicherung
            await loadVersicherung(antrag.id);
            // Reload Verwertungsnachweis bei Außerbetriebsetzung
            if (refreshedAntrag.geschaeftsvorfallTyp === 'AUSSERBETRIEBSETZUNG' && refreshedAntrag.fahrzeug?.id) {
              await loadVerwertungsnachweis(refreshedAntrag.fahrzeug.id);
            }
          } catch (fahrzeugErr) {
            console.error('Fehler beim Speichern der Fahrzeugdaten:', fahrzeugErr);
            setError('Fahrzeugdaten konnten nicht gespeichert werden. Bitte erneut versuchen.');
          }
          break;

        case 'kennzeichendaten':
          try {
            let currentFahrzeug: Fahrzeug | null | undefined = antrag.fahrzeug;

            if (!currentFahrzeug || !currentFahrzeug.id) {
              currentFahrzeug = await FahrzeugService.getFahrzeugByZulassungsantragId(antrag.id);
            }

            if (!currentFahrzeug || !currentFahrzeug.id) {
              throw new Error('Fahrzeug konnte nicht geladen werden.');
            }

            const fahrzeugId = currentFahrzeug.id;
            let fahrzeugState = currentFahrzeug;
            const isAusserbetriebsetzung = antrag.geschaeftsvorfallTyp === 'AUSSERBETRIEBSETZUNG';
            const isWiederzulassung = antrag.geschaeftsvorfallTyp === 'WIEDERZULASSUNG' || antrag.geschaeftsvorfallTyp === 'WIEDERZULASSUNG_SELBER_HALTER';
            const isUmschreibung =
              antrag.geschaeftsvorfallTyp === 'UMSCHREIBUNG_ANDERER_BEZIRK_OHNE_HALTERWECHSEL' ||
              antrag.geschaeftsvorfallTyp === 'UMSCHREIBUNG_ANDERER_BEZIRK_MIT_HALTERWECHSEL' ||
              antrag.geschaeftsvorfallTyp === 'UMSCHREIBUNG_INNERHALB_MIT_HALTERWECHSEL' ||
              antrag.geschaeftsvorfallTyp === 'AENDERUNG_HALTERDATEN';
            // Außerbetriebsetzung, Wiederzulassung und Umschreibung benötigen beide Siegel-Sicherheitscodes (vorn/hinten)
            const needsSiegelCodes = isAusserbetriebsetzung || isWiederzulassung || isUmschreibung;

            const hasNewKennzeichen = !!fd.hasNewKennzeichen;

            // Handle new license plate
            if (hasNewKennzeichen) {
              const neuesKennzeichenPayload = buildKennzeichenPayload('new');
              if (neuesKennzeichenPayload) {
                if (fahrzeugState.neuesKennzeichen?.id) {
                  const updatedKennzeichen = await FahrzeugService.updateNeuesKennzeichen(
                    fahrzeugId,
                    fahrzeugState.neuesKennzeichen.id,
                    neuesKennzeichenPayload
                  );
                  fahrzeugState = { ...fahrzeugState, neuesKennzeichen: updatedKennzeichen };
                } else {
                  const createdKennzeichen = await FahrzeugService.createNeuesKennzeichen(
                    fahrzeugId,
                    neuesKennzeichenPayload
                  );
                  fahrzeugState = { ...fahrzeugState, neuesKennzeichen: createdKennzeichen };
                }
              }
            } else if (fahrzeugState.neuesKennzeichen?.id) {
              await FahrzeugService.deleteNeuesKennzeichen(fahrzeugId);
              fahrzeugState = { ...fahrzeugState, neuesKennzeichen: undefined };
            }

            // Handle old license plate(s)
            if (needsSiegelCodes) {
              // Handle front old license plate (vorn)
              const hasOldKennzeichenVorn = !!fd.hasOldKennzeichenVorn;
              if (hasOldKennzeichenVorn) {
                const altesKennzeichenVornPayload = buildKennzeichenPayload('oldVorn');
                if (altesKennzeichenVornPayload) {
                  if (fahrzeugState.altesKennzeichenVorn?.id) {
                    const updatedOldKennzeichenVorn = await FahrzeugService.updateAltesKennzeichenVorn(
                      fahrzeugId,
                      fahrzeugState.altesKennzeichenVorn.id,
                      altesKennzeichenVornPayload
                    );
                    fahrzeugState = { ...fahrzeugState, altesKennzeichenVorn: updatedOldKennzeichenVorn };
                  } else {
                    const createdOldKennzeichenVorn = await FahrzeugService.createAltesKennzeichenVorn(
                      fahrzeugId,
                      altesKennzeichenVornPayload
                    );
                    fahrzeugState = { ...fahrzeugState, altesKennzeichenVorn: createdOldKennzeichenVorn };
                  }
                }
              } else if (fahrzeugState.altesKennzeichenVorn?.id) {
                await FahrzeugService.deleteAltesKennzeichenVorn(fahrzeugId);
                fahrzeugState = { ...fahrzeugState, altesKennzeichenVorn: undefined };
              }

              // Handle back old license plate (hinten)
              const hasOldKennzeichenHinten = !!fd.hasOldKennzeichenHinten;
              if (hasOldKennzeichenHinten) {
                const altesKennzeichenHintenPayload = buildKennzeichenPayload('oldHinten');
                if (altesKennzeichenHintenPayload) {
                  if (fahrzeugState.altesKennzeichenHinten?.id) {
                    const updatedOldKennzeichenHinten = await FahrzeugService.updateAltesKennzeichenHinten(
                      fahrzeugId,
                      fahrzeugState.altesKennzeichenHinten.id,
                      altesKennzeichenHintenPayload
                    );
                    fahrzeugState = { ...fahrzeugState, altesKennzeichenHinten: updatedOldKennzeichenHinten };
                  } else {
                    const createdOldKennzeichenHinten = await FahrzeugService.createAltesKennzeichenHinten(
                      fahrzeugId,
                      altesKennzeichenHintenPayload
                    );
                    fahrzeugState = { ...fahrzeugState, altesKennzeichenHinten: createdOldKennzeichenHinten };
                  }
                }
              } else if (fahrzeugState.altesKennzeichenHinten?.id) {
                await FahrzeugService.deleteAltesKennzeichenHinten(fahrzeugId);
                fahrzeugState = { ...fahrzeugState, altesKennzeichenHinten: undefined };
              }
            } else {
              // For other business process types: single old license plate
              const hasOldKennzeichen = !!fd.hasOldKennzeichen;
              if (hasOldKennzeichen) {
                const altesKennzeichenPayload = buildKennzeichenPayload('old');
                if (altesKennzeichenPayload) {
                  if (fahrzeugState.altesKennzeichen?.id) {
                    const updatedOldKennzeichen = await FahrzeugService.updateAltesKennzeichen(
                      fahrzeugId,
                      fahrzeugState.altesKennzeichen.id,
                      altesKennzeichenPayload
                    );
                    fahrzeugState = { ...fahrzeugState, altesKennzeichen: updatedOldKennzeichen };
                  } else {
                    const createdOldKennzeichen = await FahrzeugService.createAltesKennzeichen(
                      fahrzeugId,
                      altesKennzeichenPayload
                    );
                    fahrzeugState = { ...fahrzeugState, altesKennzeichen: createdOldKennzeichen };
                  }
                }
              } else if (fahrzeugState.altesKennzeichen?.id) {
                await FahrzeugService.deleteAltesKennzeichen(fahrzeugId);
                fahrzeugState = { ...fahrzeugState, altesKennzeichen: undefined };
              }
            }

            // Fahrzeugbezogene Kennzeichenreservierung speichern (nur bei Außerbetriebsetzung)
            if (isAusserbetriebsetzung) {
              const newReservierung = !!fd.kennzeichenReservierung;
              if (newReservierung !== antrag.kennzeichenReservierung) {
                console.log('Aktualisiere Kennzeichenreservierung:', newReservierung);
                await ZulassungsantragService.updateKennzeichenReservierung(antrag.id, newReservierung);
              }
            }

            const refreshedAntrag = await ZulassungsantragService.getZulassungsantragById(antrag.id);
            setAntrag(refreshedAntrag);
          } catch (kennzeichenErr) {
            console.error('Fehler beim Aktualisieren der Kennzeichendaten:', kennzeichenErr);
            setError('Kennzeichendaten konnten nicht gespeichert werden. Bitte erneut versuchen.');
          }
          break;
          
        case 'halterdaten':
          // Update Halter object via specific API
          try {
            if (antrag.halter && antrag.halter.id) {
              console.log('Aktualisiere Halterdaten über HalterService für Halter ID:', antrag.halter.id);
              await HalterService.updateHalter(antrag.halter.id, {
                personTyp: fd.personTyp,
                vorname: fd.vorname,
                nachname: fd.nachname,
                firma: fd.firma,
                geburtsdatum: fd.geburtsdatum,
                geburtsort: fd.geburtsort,
                geschlecht: fd.geschlecht,
                strasse: fd.strasse,
                hausnummer: fd.hausnummer,
                adresszusatz: fd.adresszusatz,
                plz: fd.plz,
                ort: fd.ort,
                land: fd.land,
                telefon: antrag.halter.telefon || '',
                iban: fd.iban,
                email: fd.email
              });
              console.log('Halterdaten erfolgreich über HalterService aktualisiert');
            } else {
              // If no halter ID, create a new halter and update the zulassungsantrag with the new halter ID
              console.log('Kein Halter ID gefunden, erstelle neuen Halter');
              const newHalter = await HalterService.createHalter({
                personTyp: fd.personTyp,
                vorname: fd.vorname,
                nachname: fd.nachname,
                firma: fd.firma,
                geburtsdatum: fd.geburtsdatum,
                geburtsort: fd.geburtsort,
                geschlecht: fd.geschlecht,
                strasse: fd.strasse,
                hausnummer: fd.hausnummer,
                adresszusatz: fd.adresszusatz,
                plz: fd.plz,
                ort: fd.ort,
                land: fd.land,
                iban: fd.iban,
                email: fd.email
              });
              console.log('Neuer Halter erstellt mit ID:', newHalter.id);
              
              // Update the zulassungsantrag with the new halter ID
              if (newHalter.id) {
                console.log('Verknüpfe Zulassungsantrag mit neuem Halter ID:', newHalter.id);
                // Link the new halter to the zulassungsantrag using the dedicated endpoint
                try {
                  // Use the new specific endpoint: PUT /api/zulassungsantraege/{id}/halter/{halterId}
                  const response = await apiClient.put(`/zulassungsantraege/${antrag.id}/halter/${newHalter.id}`);
                  
                  if (response.status === 200 || response.status === 204) {
                    console.log('Zulassungsantrag erfolgreich mit Halter verknüpft');
                  } else {
                    console.error('Fehler beim Verknüpfen:', response.statusText);
                  }
                } catch (linkError) {
                  console.error('Fehler beim Verknüpfen des Halters:', linkError);
                }
              }
            }
            // Reload the full antrag to get updated data
            const refreshedAntrag = await ZulassungsantragService.getZulassungsantragById(antrag.id);
            setAntrag(refreshedAntrag);
          } catch (halterErr) {
            console.error('Fehler beim Aktualisieren der Halterdaten über HalterService:', halterErr);
            // Fallback to updating via main antrag API
            console.warn('Fallback: Verwende ZulassungsantragService für Halterdaten-Update');
            updatedData = await ZulassungsantragService.updateZulassungsantrag(antrag.id, fd);
            setAntrag(updatedData);
          }
          break;
          
        case 'versandadresse':
          // Update Halter object via specific API
          try {
            if (antrag.halter && antrag.halter.id) {
              console.log('Aktualisiere Halterdaten über HalterService für Halter ID:', antrag.halter.id);
              await HalterService.updateHalter(antrag.halter.id, {
                personTyp: antrag.halter.personTyp,
                vorname: antrag.halter.vorname,
                nachname: antrag.halter.nachname,
                firma: antrag.halter.firma,
                geburtsdatum: antrag.halter.geburtsdatum,
                geburtsort: antrag.halter.geburtsort,
                geschlecht: antrag.halter.geschlecht,
                strasse: antrag.halter.strasse,
                hausnummer: antrag.halter.hausnummer,
                adresszusatz: antrag.halter.adresszusatz,
                plz: antrag.halter.plz,
                ort: antrag.halter.ort,
                land: antrag.halter.land,
                telefon: antrag.halter.telefon,
                iban: antrag.halter.iban || '',
                email: antrag.halter.email
              });
              console.log('Halterdaten erfolgreich über HalterService aktualisiert');
            } else {
              // If no halter ID, create a new halter and update the zulassungsantrag with the new halter ID
              console.log('Kein Halter ID gefunden, nichts zu aktualisieren');
            }
            // Reload the full antrag to get updated data
            const refreshedAntrag = await ZulassungsantragService.getZulassungsantragById(antrag.id);
            setAntrag(refreshedAntrag);
          } catch (halterErr) {
            console.error('Fehler beim Aktualisieren der Halterdaten über HalterService:', halterErr);
            // Fallback to updating via main antrag API
            console.warn('Fallback: Verwende ZulassungsantragService für Halterdaten-Update');
            updatedData = await ZulassungsantragService.updateZulassungsantrag(antrag.id, fd);
            setAntrag(updatedData);
          }
          break;

        case 'verwertungsnachweis':
          // Validierung vor dem Speichern
          const isAuslandInvalid = fd.staatVerwertung === 'D' && fd.merkmalVerwertungsnachweis === '2';
          const isBetriebsnummerInvalid = fd.merkmalVerwertungsnachweis === '0' && !fd.betriebsnummerDemontagebetrieb;
          
          if (isAuslandInvalid) {
            setError('Bei Staat "Deutschland" ist "Verwertung ins Ausland" nicht zulässig.');
            setSaving(false);
            return;
          }
          
          if (isBetriebsnummerInvalid) {
            setError('Betriebsnummer ist erforderlich wenn Verwertungsnachweis vorlag.');
            setSaving(false);
            return;
          }
          
          // Update Verwertungsnachweis via FahrzeugService
          try {
            if (antrag.fahrzeug && antrag.fahrzeug.id) {
              console.log('Aktualisiere Verwertungsnachweis für Fahrzeug ID:', antrag.fahrzeug.id);
              const updatedNachweis = await FahrzeugService.updateVerwertungsnachweis(antrag.fahrzeug.id, {
                betriebsnummerDemontagebetrieb: fd.betriebsnummerDemontagebetrieb,
                datumAusstellung: fd.datumAusstellung,
                merkmalVerwertungsnachweis: fd.merkmalVerwertungsnachweis,
                staatVerwertung: fd.staatVerwertung
              });
              setVerwertungsnachweis(updatedNachweis);
              console.log('Verwertungsnachweis erfolgreich aktualisiert');
            } else {
              console.error('Kein Fahrzeug ID gefunden, Verwertungsnachweis kann nicht gespeichert werden');
              setError('Fahrzeug muss zuerst angelegt werden, bevor Verwertungsnachweis gespeichert werden kann.');
            }
          } catch (verwertungErr) {
            console.error('Fehler beim Aktualisieren des Verwertungsnachweises:', verwertungErr);
            setError('Verwertungsnachweis konnte nicht gespeichert werden. Bitte erneut versuchen.');
          }
          break;

        default:
          // For sections without specific APIs, use the main antrag update
          updatedData = await ZulassungsantragService.updateZulassungsantrag(antrag.id, fd);
          setAntrag(updatedData);
          break;
      }
      
      handleCloseDialog();
      
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setError('Fehler beim Speichern der Änderungen');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVerwertungsnachweis = async () => {
    if (!antrag || !antrag.fahrzeug?.id) {
      setError('Kein Fahrzeug vorhanden');
      return;
    }
    
    if (!window.confirm('Möchten Sie die Verwertungsnachweisdaten wirklich löschen?')) {
      return;
    }
    
    try {
      await FahrzeugService.deleteVerwertungsnachweis(antrag.fahrzeug.id);
      setVerwertungsnachweis(null);
      console.log('Verwertungsnachweis erfolgreich gelöscht');
    } catch (err) {
      console.error('Fehler beim Löschen des Verwertungsnachweises:', err);
      setError('Verwertungsnachweis konnte nicht gelöscht werden');
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setEditFormData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const formatKennzeichen = (kennzeichen?: Kennzeichen | null) => {
    if (!kennzeichen) {
      return 'Nicht vorhanden';
    }

    const parts = [kennzeichen.bezirk, kennzeichen.buchstaben, kennzeichen.nummer]
      .filter((part) => !!part && String(part).trim().length > 0)
      .map((part) => String(part).trim());

    if (!parts.length) {
      return 'Nicht vorhanden';
    }

    let result = parts.join(' ');

    if (kennzeichen.sonderkennzeichen) {
      result = `${result} ${kennzeichen.sonderkennzeichen}`;
    }

    return result;
  };

  const buildKennzeichenPayload = (prefix: 'old' | 'new' | 'oldVorn' | 'oldHinten'): KennzeichenCreateRequest | null => {
    const fieldPrefix = prefix === 'old' ? 'oldKennzeichen' 
      : prefix === 'oldVorn' ? 'oldKennzeichenVorn'
      : prefix === 'oldHinten' ? 'oldKennzeichenHinten'
      : 'newKennzeichen';

    const bezirk = editFormData[`${fieldPrefix}Bezirk`]?.trim();
    const buchstaben = editFormData[`${fieldPrefix}Buchstaben`]?.trim();
    const nummer = editFormData[`${fieldPrefix}Nummer`]?.trim();

    if (!bezirk || !buchstaben || !nummer) {
      return null;
    }

    const sonderkennzeichenRaw = editFormData[`${fieldPrefix}Sonderkennzeichen`]?.trim?.();
    const sonderkennzeichen = sonderkennzeichenRaw ? sonderkennzeichenRaw : undefined;

    // Saison und Reservierung nur für neue Kennzeichen
    if (prefix === 'new') {
      const saisonVonRaw = editFormData[`${fieldPrefix}SaisonVon`]?.trim?.();
      const saisonBisRaw = editFormData[`${fieldPrefix}SaisonBis`]?.trim?.();
      const reserviert = !!editFormData[`${fieldPrefix}Reserviert`];
      const reservierungsPinRaw = editFormData[`${fieldPrefix}ReservierungPin`]?.trim?.();

      return {
        bezirk,
        buchstaben,
        nummer,
        sonderkennzeichen: sonderkennzeichen === '' ? undefined : sonderkennzeichen,
        saisonVon: saisonVonRaw === '' ? undefined : saisonVonRaw,
        saisonBis: saisonBisRaw === '' ? undefined : saisonBisRaw,
        reserviert,
        reservierungspin: reservierungsPinRaw === '' ? undefined : reservierungsPinRaw
      };
    }

    // For old license plates with position (AUSSERBETRIEBSETZUNG)
    if (prefix === 'oldVorn' || prefix === 'oldHinten') {
      const siegelSicherheitscodeRaw = editFormData[`${fieldPrefix}SiegelSicherheitscode`]?.trim?.();
      const position = prefix === 'oldVorn' ? 'VORN' : 'HINTEN';

      return {
        bezirk,
        buchstaben,
        nummer,
        sonderkennzeichen: sonderkennzeichen === '' ? undefined : sonderkennzeichen,
        position,
        siegelSicherheitscode: siegelSicherheitscodeRaw === '' ? undefined : siegelSicherheitscodeRaw
      };
    }

    return {
      bezirk,
      buchstaben,
      nummer,
      sonderkennzeichen: sonderkennzeichen === '' ? undefined : sonderkennzeichen
    };
  };

  const handleBack = () => {
    navigate('/zulassungsantraege');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status.toUpperCase()) {
      case 'ERSTELLT':
        return 'default';
      case 'GESENDET':
        return 'info';
      case 'IN_BEARBEITUNG':
        return 'warning';
      case 'GEPRUEFT':
        return 'primary';
      case 'ABGESCHLOSSEN':
      case 'ABGESCHLOSSEN_ERFOLGREICH':
        return 'success';
      case 'ABGESCHLOSSEN_FEHLERHAFT':
      case 'ABGELEHNT':
      case 'FEHLER':
        return 'error';
      case 'STORNIERT':
        return 'secondary';
      default:
        return 'default';
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Zurück zur Übersicht
        </Button>
      </Box>
    );
  }

  if (!antrag) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Antrag nicht gefunden
        </Alert>
        <Button onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Zurück zur Übersicht
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">
            Zulassungsantrag Details
          </Typography>
          <Chip 
            label={`ID: ${antrag.id}`} 
            color="primary" 
            variant="outlined" 
          />
        </Box>
        {/* KBA-Aktionen */}
        <Box display="flex" gap={1}>
          {/* An KBA senden oder QES Unterschreiben - nur wenn Status ERSTELLT */}
          {antrag.status === 'ERSTELLT' && (
            antrag.geschaeftsvorfallTyp === 'AUSSERBETRIEBSETZUNG' || antrag.antragstellerTyp === 'EIGENE_PERSON' ? (
              <Tooltip title="Antrag an KBA senden">
                <Button
                  variant="contained"
                  color="success"
                  startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  onClick={handleSubmitToKba}
                  disabled={submitting}
                >
                  {submitting ? 'Sende...' : 'An KBA senden'}
                </Button>
              </Tooltip>
            ) : (
              <>
                <Tooltip title="Antrag qualifiziert elektronisch signieren">
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <DrawIcon />}
                    onClick={handleQesSignieren}
                    disabled={submitting}
                  >
                    {submitting ? 'Signiere...' : 'QES Unterschreiben'}
                  </Button>
                </Tooltip>
                <Tooltip title="Antrag an KBA senden">
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    onClick={handleSubmitToKba}
                    disabled={submitting}
                  >
                    {submitting ? 'Sende...' : 'An KBA senden'}
                  </Button>
                </Tooltip>
              </>
            )
          )}
          {/* KBA-Status abfragen - nur wenn bereits gesendet */}
          {['GESENDET', 'IN_BEARBEITUNG'].includes(antrag.status) && (
            <Tooltip title="Aktuellen Status beim KBA abfragen">
              <Button
                variant="outlined"
                color="primary"
                startIcon={queryingStatus ? <CircularProgress size={20} /> : <RefreshIcon />}
                onClick={handleQueryKbaStatus}
                disabled={queryingStatus}
              >
                {queryingStatus ? 'Abfrage...' : 'Status abfragen'}
              </Button>
            </Tooltip>
          )}
          {/* Workflow neu starten - nur bei Status FEHLER */}
          {antrag.status === 'FEHLER' && (
            <Tooltip title="Automatischen Workflow neu starten">
              <Button
                variant="contained"
                color="warning"
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                onClick={handleRestartWorkflow}
                disabled={submitting}
              >
                {submitting ? 'Starte...' : 'Workflow neu starten'}
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Erfolgs-Meldung */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage(null)}
          icon={<CheckCircleIcon />}
        >
          {successMessage}
        </Alert>
      )}

      {/* Submit-Fehler */}
      {submitError && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setSubmitError(null)}
          icon={<ErrorIcon />}
        >
          <Typography variant="subtitle2" fontWeight="bold">Fehler beim Senden:</Typography>
          {submitError}
        </Alert>
      )}

      {/* Status-Abfrage Fehler */}
      {statusError && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setStatusError(null)}
          icon={<ErrorIcon />}
        >
          <Typography variant="subtitle2" fontWeight="bold">Fehler bei Statusabfrage:</Typography>
          {statusError}
        </Alert>
      )}

      {/* KBA-Quittung anzeigen (wenn vorhanden) */}
      {submitResult?.quittung && (
        <Alert 
          severity={submitResult.quittung.success ? 'success' : 'warning'} 
          sx={{ mb: 2 }}
          onClose={() => setSubmitResult(null)}
        >
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            KBA-Quittung:
          </Typography>
          {submitResult.quittung.antragsnummer && (
            <Typography variant="body2">
              <strong>Antragsnummer:</strong> {submitResult.quittung.antragsnummer}
            </Typography>
          )}
          {submitResult.quittung.quittungsCodes && submitResult.quittung.quittungsCodes.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" fontWeight="bold">Quittungscodes:</Typography>
              {submitResult.quittung.quittungsCodes.map((qc, idx) => (
                <Chip 
                  key={idx}
                  label={`${qc.code}${qc.text ? `: ${qc.text}` : ''}`}
                  size="small"
                  color={qc.quittungsart === 0 ? 'success' : qc.quittungsart === 2 ? 'error' : 'default'}
                  sx={{ mr: 0.5, mt: 0.5 }}
                />
              ))}
            </Box>
          )}
          {submitResult.quittung.fehlerMeldungen && submitResult.quittung.fehlerMeldungen.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" fontWeight="bold" color="error">Fehlermeldungen:</Typography>
              {submitResult.quittung.fehlerMeldungen.map((msg, idx) => (
                <Typography key={idx} variant="body2" color="error">• {msg}</Typography>
              ))}
            </Box>
          )}
        </Alert>
      )}

      {/* Status-Abfrage Ergebnis anzeigen */}
      {statusResult && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          onClose={() => setStatusResult(null)}
          icon={<InfoIcon />}
        >
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            KBA-Statusabfrage Ergebnis:
          </Typography>
          {statusResult.kbaAntragsnummer && (
            <Typography variant="body2">
              <strong>KBA-Antragsnummer:</strong> {statusResult.kbaAntragsnummer}
            </Typography>
          )}
          {statusResult.kbaStatus && (
            <Typography variant="body2">
              <strong>KBA-Status:</strong> {statusResult.kbaStatus}
            </Typography>
          )}
          {statusResult.statusBeschreibung && (
            <Typography variant="body2">
              <strong>Beschreibung:</strong> {statusResult.statusBeschreibung}
            </Typography>
          )}
          {statusResult.neuerStatus && (
            <Typography variant="body2" color="success.main">
              <strong>Neuer Status:</strong> {statusResult.neuerStatus}
            </Typography>
          )}
          {statusResult.fehlerMeldung && (
            <Typography variant="body2" color="error">
              <strong>Fehler:</strong> {statusResult.fehlerMeldung}
            </Typography>
          )}
        </Alert>
      )}

      {/* Ablehnungsgrund anzeigen wenn Status ABGELEHNT oder FEHLER */}
      {antrag && (antrag.status === 'ABGELEHNT' || antrag.status === 'FEHLER') && antrag.ablehnungsgrund && (
        <AblehnungsgrundCard ablehnungsgrund={antrag.ablehnungsgrund} />
      )}

      <Grid container spacing={2}>
        {/* Linke Seite: Antragsdaten */}
        <Grid item xs={12} md={9}>
          <Grid container spacing={2}>
            {/* Grunddaten */}
            <Grid item xs={12} sm={6} md={4}>
              <GrunddatenCard
                antrag={antrag}
                onEdit={() => handleOpenDialog('grunddaten')}
                getStatusColor={getStatusColor}
                formatDate={formatDate}
                vollmachtExists={vollmachtExists}
                sepaExists={sepaExists}
              />
            </Grid>

            {/* Fahrzeugdaten */}
            <Grid item xs={12} sm={6} md={4}>
              <FahrzeugdatenCard
                antrag={antrag}
                onEdit={() => handleOpenDialog('fahrzeugdaten')}
                evbNummer={versicherung?.evbNummer}
              />
            </Grid>

            {/* Halterdaten - nur wenn für Geschäftsvorfall und Antragstellertyp erforderlich */}
            {fieldVisibility.halter && (
              <Grid item xs={12} sm={6} md={4}>
                <HalterdatenCard
                  antrag={antrag}
                  onEdit={() => handleOpenDialog('halterdaten')}
                />
              </Grid>
            )}

            {/* Kennzeichendaten */}
            {componentVisibility.kennzeichen && (
              <Grid item xs={12} sm={6} md={4}>
                <KennzeichendatenCard
                  antrag={antrag}
                  onEdit={() => handleOpenDialog('kennzeichendaten')}
                  formatKennzeichen={formatKennzeichen}
                />
              </Grid>
            )}


            {/* Verwertungsnachweis - nur bei Außerbetriebsetzung */}
            {fieldVisibility.verwertungsnachweis && (
              <Grid item xs={12} sm={6} md={4}>
                <VerwertungsnachweisCard
                  verwertungsnachweis={verwertungsnachweis}
                  onEdit={() => handleOpenDialog('verwertungsnachweis')}
                  onDelete={handleDeleteVerwertungsnachweis}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Rechte Seite: Checkliste für benötigte Daten */}
        <Grid item xs={12} md={3}>
          <RequiredDataChecklist
            antrag={antrag}
            vollmachtExists={vollmachtExists}
            sepaExists={sepaExists}
            evbNummer={versicherung?.evbNummer}
            verwertungsnachweis={verwertungsnachweis}
          />
        </Grid>
      </Grid>

      {/* SABRINA Empfangene Nachrichten */}
      {antrag && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MailIcon color="primary" />
            SABRINA-Nachrichten
            {sabrinaNachrichten.length > 0 && (
              <Chip
                size="small"
                label={`${sabrinaNachrichten.length} empfangen`}
                color="success"
              />
            )}
          </Typography>

          {sabrinaLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : sabrinaNachrichten.length === 0 ? (
            <Alert severity="info" variant="outlined">
              Keine empfangenen SABRINA-Nachrichten für diesen Antrag.
            </Alert>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {sabrinaNachrichten.map((nachricht) => (
                <Box
                  key={nachricht.id}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    p: 2
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2">
                      {nachricht.kommunikationsschrittName}
                    </Typography>
                    <Chip
                      size="small"
                      label={nachricht.antragStatus || 'Unbekannt'}
                      color={nachricht.antragStatus === 'bearbeitet' ? 'success' : 'default'}
                    />
                  </Box>
                  <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={1} mb={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Bearbeitungsart</Typography>
                      <Typography variant="body2">{nachricht.bearbeitungsart || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Gebühren</Typography>
                      <Typography variant="body2">
                        {nachricht.gebuehrenGesamt != null ? `${nachricht.gebuehrenGesamt.toFixed(2)} €` : '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Wirksamkeit</Typography>
                      <Typography variant="body2">
                        {nachricht.datumWirksamkeit
                          ? new Date(nachricht.datumWirksamkeit).toLocaleDateString('de-DE')
                          : '-'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Ablehnungsgrund + Prozesshinweis */}
                  {(nachricht.ablehnungsgrundName || nachricht.ablehnungsgrundCode || nachricht.prozesshinweis) && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1.5,
                        bgcolor: 'error.50',
                        border: '1px solid',
                        borderColor: 'error.200',
                        borderRadius: 1
                      }}
                    >
                      {(nachricht.ablehnungsgrundName || nachricht.ablehnungsgrundCode) && (
                        <Box mb={nachricht.prozesshinweis ? 1 : 0}>
                          <Typography variant="caption" color="error.main" fontWeight="bold">
                            Ablehnungsgrund
                          </Typography>
                          <Typography variant="body2" color="error.dark">
                            {nachricht.ablehnungsgrundName || ''}
                            {nachricht.ablehnungsgrundCode && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                [Code: {nachricht.ablehnungsgrundCode}]
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                      )}
                      {nachricht.prozesshinweis && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight="bold">
                            Prozesshinweis
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {nachricht.prozesshinweis}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {nachricht.dokumente?.length > 0 && (
                    <Box mt={1}>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Dokumente
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
                        {nachricht.dokumente.map((dok) => (
                          <Button
                            key={dok.id}
                            variant="outlined"
                            size="small"
                            startIcon={<PdfIcon />}
                            onClick={() => handleDownloadSabrinaDokument(dok.id)}
                          >
                            {dok.artDesDokuments}
                            {dok.dokumentInformation && ` (${dok.dokumentInformation})`}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Edit Dialogs */}
      {renderEditDialog()}
    </Box>
  );

  function renderEditDialog() {
    if (!openDialog) return null;

    const getDialogTitle = () => {
      switch (openDialog) {
        case 'grunddaten':
          return 'Grunddaten bearbeiten';
        case 'fahrzeugdaten':
          return 'Fahrzeugdaten bearbeiten';
        case 'kennzeichendaten':
          return 'Kennzeichendaten bearbeiten';
        case 'versandadresse':
          return 'Versandadresse bearbeiten';
        case 'halterdaten':
          return 'Halterdaten bearbeiten';
        case 'verwertungsnachweis':
          return 'Verwertungsnachweis bearbeiten';
        default:
          return 'Bearbeiten';
      }
    };

    const renderDialogContent = () => {
      switch (openDialog) {
        case 'grunddaten':
          return (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editFormData.status || ''}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="ERSTELLT">Erstellt</MenuItem>
                    <MenuItem value="GESENDET">Gesendet</MenuItem>
                    <MenuItem value="IN_BEARBEITUNG">In Bearbeitung</MenuItem>
                    <MenuItem value="GEPRUEFT">Geprüft</MenuItem>
                    <MenuItem value="ABGESCHLOSSEN">Abgeschlossen</MenuItem>
                    <MenuItem value="ABGELEHNT">Abgelehnt</MenuItem>
                    <MenuItem value="FEHLER">Fehler</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Geschäftsvorfall-Typ</InputLabel>
                  <Select
                    value={editFormData.geschaeftsvorfallTyp || ''}
                    onChange={(e) => handleInputChange('geschaeftsvorfallTyp', e.target.value)}
                    label="Geschäftsvorfall-Typ"
                  >
                    <MenuItem value="NEUZULASSUNG">{getGeschaeftsvorfallTypLabel('NEUZULASSUNG')}</MenuItem>
                    <MenuItem value="UMSCHREIBUNG_ANDERER_BEZIRK_OHNE_HALTERWECHSEL">{getGeschaeftsvorfallTypLabel('UMSCHREIBUNG_ANDERER_BEZIRK_OHNE_HALTERWECHSEL')}</MenuItem>
                    <MenuItem value="UMSCHREIBUNG_ANDERER_BEZIRK_MIT_HALTERWECHSEL">{getGeschaeftsvorfallTypLabel('UMSCHREIBUNG_ANDERER_BEZIRK_MIT_HALTERWECHSEL')}</MenuItem>
                    <MenuItem value="UMSCHREIBUNG_INNERHALB_MIT_HALTERWECHSEL">{getGeschaeftsvorfallTypLabel('UMSCHREIBUNG_INNERHALB_MIT_HALTERWECHSEL')}</MenuItem>
                    <MenuItem value="AUSSERBETRIEBSETZUNG">{getGeschaeftsvorfallTypLabel('AUSSERBETRIEBSETZUNG')}</MenuItem>
                    <MenuItem value="WIEDERZULASSUNG_SELBER_HALTER">{getGeschaeftsvorfallTypLabel('WIEDERZULASSUNG_SELBER_HALTER')}</MenuItem>
                    <MenuItem value="WIEDERZULASSUNG">{getGeschaeftsvorfallTypLabel('WIEDERZULASSUNG')}</MenuItem>
                    <MenuItem value="AENDERUNG_HALTERDATEN">{getGeschaeftsvorfallTypLabel('AENDERUNG_HALTERDATEN')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Dokumentenverwaltung - nur Komponenten anzeigen, die für diesen Geschäftsvorfall und Antragstellertyp relevant sind */}
              {(fieldVisibility.vollmacht || fieldVisibility.sepaMandat) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    Dokumentenverwaltung
                  </Typography>
                </Grid>
              )}
              
              {/* Vollmacht - nur wenn erforderlich */}
              {fieldVisibility.vollmacht && (
                <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">Vollmacht</Typography>
                    <Chip 
                      label={vollmachtExists ? 'Vorhanden' : 'Nicht vorhanden'} 
                      color={vollmachtExists ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {vollmachtExists ? (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenDocument('vollmacht')}
                          disabled={loadingDocuments}
                        >
                          Öffnen
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleDeleteDocument('vollmacht')}
                          disabled={loadingDocuments}
                        >
                          Löschen
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleGenerateDocument('vollmacht')}
                        disabled={loadingDocuments}
                      >
                        Generieren
                      </Button>
                    )}
                  </Box>
                </Box>
              </Grid>
              )}
              
              {/* SEPA-Mandat - nur wenn erforderlich */}
              {fieldVisibility.sepaMandat && (
                <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">SEPA-Mandat</Typography>
                    <Chip 
                      label={sepaExists ? 'Vorhanden' : 'Nicht vorhanden'} 
                      color={sepaExists ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {sepaExists ? (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenDocument('sepa')}
                          disabled={loadingDocuments}
                        >
                          Öffnen
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleDeleteDocument('sepa')}
                          disabled={loadingDocuments}
                        >
                          Löschen
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleGenerateDocument('sepa')}
                        disabled={loadingDocuments}
                      >
                        Generieren
                      </Button>
                    )}
                  </Box>
                </Box>
              </Grid>
              )}
            </Grid>
          );

        case 'fahrzeugdaten':
          return (
            <Grid container spacing={2}>
              {/* FIN - immer anzeigen */}
              {fieldVisibility.fin && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="FIN"
                    value={editFormData.fin || ''}
                    onChange={(e) => {
                      // Validierung: nur Großbuchstaben und Ziffern erlaubt [0-9A-Z]
                      const value = e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '');
                      handleInputChange('fin', value);
                    }}
                    inputProps={{ 
                      maxLength: 17,
                      style: { textTransform: 'uppercase' }
                    }}
                    autoComplete="off"
                    error={editFormData.fin && (editFormData.fin.length < 5 || editFormData.fin.length > 17)}
                    helperText={
                      editFormData.fin && (editFormData.fin.length < 5 || editFormData.fin.length > 17)
                        ? `FIN muss zwischen 5 und 17 Zeichen haben (aktuell: ${editFormData.fin.length})`
                        : "5- bis 17-stellige Fahrzeug-Identifizierungsnummer, nur Großbuchstaben und Ziffern"
                    }
                  />
                </Grid>
              )}
              {/*
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Hersteller"
                  value={editFormData.hersteller || ''}
                  onChange={(e) => handleInputChange('hersteller', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Modell"
                  value={editFormData.modell || ''}
                  onChange={(e) => handleInputChange('modell', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Marke"
                  value={editFormData.marke || ''}
                  onChange={(e) => handleInputChange('marke', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Verwendungszweck"
                  value={editFormData.verwendungszweck || ''}
                  onChange={(e) => handleInputChange('verwendungszweck', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="HSN"
                  value={editFormData.hsn || ''}
                  onChange={(e) => handleInputChange('hsn', e.target.value)}
                  helperText="Herstellerschlüsselnummer"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="TSN"
                  value={editFormData.tsn || ''}
                  onChange={(e) => handleInputChange('tsn', e.target.value)}
                  helperText="Typschlüsselnummer"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Kennzeichentyp</InputLabel>
                  <Select
                    value={editFormData.kennzeichenTyp || ''}
                    onChange={(e) => handleInputChange('kennzeichenTyp', e.target.value)}
                    label="Kennzeichentyp"
                  >
                    <MenuItem value="">Nicht angegeben</MenuItem>
                    <MenuItem value={KennzeichenTyp.STANDARD}>Standard</MenuItem>
                    <MenuItem value={KennzeichenTyp.SAISON}>Saison</MenuItem>
                    <MenuItem value={KennzeichenTyp.OLDTIMER}>Oldtimer</MenuItem>
                    <MenuItem value={KennzeichenTyp.E_KENNZEICHEN}>E-Kennzeichen</MenuItem>
                    <MenuItem value={KennzeichenTyp.KURZZEITKENNZEICHEN}>Kurzzeitkennzeichen</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Saison von (Monat)"
                  type="number"
                  value={editFormData.saisonVon || ''}
                  onChange={(e) => {
                    const value = e.target.value ? parseInt(e.target.value) : null;
                    setEditFormData((prev: any) => ({ ...prev, saisonVon: value }));
                  }}
                  inputProps={{ min: 1, max: 12 }}
                  helperText="Monat 1-12"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Saison bis (Monat)"
                  type="number"
                  value={editFormData.saisonBis || ''}
                  onChange={(e) => {
                    const value = e.target.value ? parseInt(e.target.value) : null;
                    setEditFormData((prev: any) => ({ ...prev, saisonBis: value }));
                  }}
                  inputProps={{ min: 1, max: 12 }}
                  helperText="Monat 1-12"
                />
              </Grid>
              */}
              {/* Elektrofahrzeug */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!editFormData.elektrofahrzeug}
                      onChange={(e) => handleInputChange('elektrofahrzeug', e.target.checked)}
                    />
                  }
                  label="Elektrofahrzeug (kein SEPA-Mandat / keine Bankverbindung erforderlich)"
                />
              </Grid>

              {/* ZB1 Felder - nur wenn erforderlich */}
              {fieldVisibility.zb1 && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="ZB1 Nummer"
                      value={editFormData.zb1Nummer || ''}
                      onChange={(e) => {
                        // Validierung: Muss mit A-Z,Ä,Ö,Ü beginnen, gefolgt von 0-9,A-Z,Ä,Ö,Ü,-,/
                        let value = e.target.value.toUpperCase();
                        // Erlaube nur gültige Zeichen
                        if (value.length === 1) {
                          value = value.replace(/[^A-ZÄÖÜ]/g, '');
                        } else if (value.length > 1) {
                          const firstChar = value[0].replace(/[^A-ZÄÖÜ]/g, '');
                          const restChars = value.slice(1).replace(/[^0-9A-ZÄÖÜ\-\/]/g, '');
                          value = firstChar + restChars;
                        }
                        handleInputChange('zb1Nummer', value);
                      }}
                      inputProps={{ 
                        maxLength: 20,
                        style: { textTransform: 'uppercase' }
                      }}
                      autoComplete="off"
                      error={editFormData.zb1Nummer && (editFormData.zb1Nummer.length < 18 || editFormData.zb1Nummer.length > 20)}
                      helperText={
                        editFormData.zb1Nummer && (editFormData.zb1Nummer.length < 18 || editFormData.zb1Nummer.length > 20)
                          ? `ZB1 muss 18-20 Zeichen haben (aktuell: ${editFormData.zb1Nummer.length})`
                          : "18-20 Zeichen, beginnt mit Buchstabe, dann 0-9,A-Z,Ä,Ö,Ü,-,/"
                      }
                    />
                  </Grid>
                  {(
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="ZB1 Sicherheitscode"
                        value={editFormData.zb1Sicherheitscode || ''}
                        onChange={(e) => {
                          handleInputChange('zb1Sicherheitscode', e.target.value);
                        }}
                        inputProps={{ 
                          maxLength: 20
                        }}
                        autoComplete="off"
                        helperText="1-20 Zeichen, Sicherheitscode der ZB Teil I (Groß-/Kleinschreibung)"
                      />
                    </Grid>
                  )}
                </>
              )}
              
              {/* ZB2 Felder - nur wenn erforderlich */}
              {fieldVisibility.zb2 && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="ZB2 Nummer"
                      value={editFormData.zb2Nummer || ''}
                      onChange={(e) => {
                        // Validierung: [0-9A-Z]* ODER "ohne-ZF-" ODER "OHNEZBII"
                        let value = e.target.value.toUpperCase();
                        // Prüfe auf Sonderwerte
                        if (value === 'OHNE-ZF-' || value === 'OHNEZBII' || value.startsWith('OHNE-') || value.startsWith('OHNEZ')) {
                          // Erlaube Sonderwerte
                          if (value !== 'OHNE-ZF-' && value !== 'OHNEZBII') {
                            value = value.replace(/[^A-Z\-]/g, '');
                          }
                        } else {
                          // Nur 0-9 und A-Z erlauben
                          value = value.replace(/[^0-9A-Z]/g, '');
                        }
                        handleInputChange('zb2Nummer', value);
                      }}
                      inputProps={{ 
                        maxLength: 20,
                        style: { textTransform: 'uppercase' }
                      }}
                      autoComplete="off"
                      helperText='1-20 Zeichen, 0-9,A-Z oder "ohne-ZF-" oder "OHNEZBII"'
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="ZB2 Sicherheitscode"
                        value={editFormData.zb2Sicherheitscode || ''}
                        onChange={(e) => {
                          // Validierung: Muss mit "B" beginnen, gefolgt von 10 alphanumerischen Zeichen, endet mit 0-9 oder X
                          let value = e.target.value;
                          
                          // Erlaube nur gültige Zeichen basierend auf Position
                          if (value.length === 1) {
                            // Erstes Zeichen muss "B" sein
                            value = value.toUpperCase() === 'B' ? 'B' : '';
                          } else if (value.length > 1 && value.length <= 11) {
                            // Zeichen 2-11: alphanumerisch (a-zA-Z0-9)
                            const firstChar = value[0] === 'B' ? 'B' : '';
                            const middleChars = value.slice(1).replace(/[^a-zA-Z0-9]/g, '');
                            value = firstChar + middleChars;
                          } else if (value.length > 11) {
                            // Zeichen 12: nur 0-9 oder X
                            const firstChar = value[0] === 'B' ? 'B' : '';
                            const middleChars = value.slice(1, 11).replace(/[^a-zA-Z0-9]/g, '');
                            const lastChar = value.slice(11, 12).replace(/[^0-9X]/gi, '').toUpperCase();
                            value = firstChar + middleChars + lastChar;
                          }
                          
                          handleInputChange('zb2Sicherheitscode', value);
                        }}
                        inputProps={{ 
                          maxLength: 12
                        }}
                        autoComplete="off"
                        error={editFormData.zb2Sicherheitscode && editFormData.zb2Sicherheitscode.length > 0 && editFormData.zb2Sicherheitscode.length !== 12}
                        helperText={
                          editFormData.zb2Sicherheitscode && editFormData.zb2Sicherheitscode.length > 0 && editFormData.zb2Sicherheitscode.length !== 12
                            ? `ZB2 Sicherheitscode muss 12 Zeichen haben (aktuell: ${editFormData.zb2Sicherheitscode.length})`
                            : "12 Zeichen: B + 10 alphanumerisch + (0-9 oder X)"
                        }
                      />
                    </Grid>
                </>
              )}
              
              {/* eVB-Nummer (Versicherungsbestätigung) - nur wenn erforderlich */}
              {fieldVisibility.versicherung && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                      Versicherungsbestätigung
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="eVB-Nummer"
                      value={editFormData.evbNummer || ''}
                      onChange={(e) => {
                        // eVB-Nummer: 7 Zeichen, alphanumerisch
                        const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                        handleInputChange('evbNummer', value);
                      }}
                      inputProps={{ 
                        maxLength: 7,
                        style: { textTransform: 'uppercase' }
                      }}
                      autoComplete="off"
                      error={editFormData.evbNummer && editFormData.evbNummer.length > 0 && editFormData.evbNummer.length !== 7}
                      helperText={
                        editFormData.evbNummer && editFormData.evbNummer.length > 0 && editFormData.evbNummer.length !== 7
                          ? `eVB-Nummer muss 7 Zeichen haben (aktuell: ${editFormData.evbNummer.length})`
                          : "7-stellige elektronische Versicherungsbestätigung"
                      }
                    />
                  </Grid>
                </>
              )}
            </Grid>
          );

        case 'kennzeichendaten':
          return (
            <Grid container spacing={2}>
              {/* Neues Kennzeichen - nur wenn erforderlich */}
              {fieldVisibility.neuesKennzeichen && (
                <>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!editFormData.hasNewKennzeichen}
                          onChange={(e) => handleInputChange('hasNewKennzeichen', e.target.checked)}
                        />
                      }
                      label="Neues Kennzeichen vorhanden"
                    />
                  </Grid>
                </>
              )}
              {fieldVisibility.neuesKennzeichen && editFormData.hasNewKennzeichen && (
                <>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Bezirk"
                      value={editFormData.newKennzeichenBezirk || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        const filtered = value.replace(/[^A-Za-zÄÖÜäöüß]/g, '').toUpperCase().slice(0, 3);
                        handleInputChange('newKennzeichenBezirk', filtered);
                      }}
                      onPaste={(e) => {
                        const parsed = parseKennzeichen(e.clipboardData.getData('text'));
                        if (parsed) {
                          e.preventDefault();
                          handleInputChange('newKennzeichenBezirk', parsed.bezirk);
                          handleInputChange('newKennzeichenBuchstaben', parsed.buchstaben);
                          handleInputChange('newKennzeichenNummer', parsed.nummer);
                        }
                      }}
                      inputProps={{ maxLength: 3 }}
                      autoComplete="off"
                      helperText="1-3 Buchstaben"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Buchstaben"
                      value={editFormData.newKennzeichenBuchstaben || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        const filtered = value.replace(/[^A-Za-zÄÖÜäöüß]/g, '').toUpperCase().slice(0, 3);
                        handleInputChange('newKennzeichenBuchstaben', filtered);
                      }}
                      onPaste={(e) => {
                        const parsed = parseKennzeichen(e.clipboardData.getData('text'));
                        if (parsed) {
                          e.preventDefault();
                          handleInputChange('newKennzeichenBezirk', parsed.bezirk);
                          handleInputChange('newKennzeichenBuchstaben', parsed.buchstaben);
                          handleInputChange('newKennzeichenNummer', parsed.nummer);
                        }
                      }}
                      inputProps={{ maxLength: 3 }}
                      autoComplete="off"
                      helperText="1-3 Buchstaben"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Nummer"
                      value={editFormData.newKennzeichenNummer || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        const filtered = value.replace(/[^0-9]/g, '').slice(0, 4);
                        handleInputChange('newKennzeichenNummer', filtered);
                      }}
                      onPaste={(e) => {
                        const parsed = parseKennzeichen(e.clipboardData.getData('text'));
                        if (parsed) {
                          e.preventDefault();
                          handleInputChange('newKennzeichenBezirk', parsed.bezirk);
                          handleInputChange('newKennzeichenBuchstaben', parsed.buchstaben);
                          handleInputChange('newKennzeichenNummer', parsed.nummer);
                        }
                      }}
                      inputProps={{ maxLength: 4 }}
                      autoComplete="off"
                      helperText="1-4 Ziffern"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel>Sonderkennzeichen</InputLabel>
                      <Select
                        value={editFormData.newKennzeichenSonderkennzeichen || ''}
                        onChange={(e) => handleInputChange('newKennzeichenSonderkennzeichen', e.target.value)}
                        label="Sonderkennzeichen"
                      >
                        <MenuItem value="">Kein</MenuItem>
                        <MenuItem value="E">E (Elektro)</MenuItem>
                        <MenuItem value="H">H (Historisch)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel>Saison Von</InputLabel>
                      <Select
                        value={editFormData.newKennzeichenSaisonVon || ''}
                        onChange={(e) => handleInputChange('newKennzeichenSaisonVon', e.target.value)}
                        label="Saison Von"
                      >
                        <MenuItem value="">Keine</MenuItem>
                        <MenuItem value="01">01 - Januar</MenuItem>
                        <MenuItem value="02">02 - Februar</MenuItem>
                        <MenuItem value="03">03 - März</MenuItem>
                        <MenuItem value="04">04 - April</MenuItem>
                        <MenuItem value="05">05 - Mai</MenuItem>
                        <MenuItem value="06">06 - Juni</MenuItem>
                        <MenuItem value="07">07 - Juli</MenuItem>
                        <MenuItem value="08">08 - August</MenuItem>
                        <MenuItem value="09">09 - September</MenuItem>
                        <MenuItem value="10">10 - Oktober</MenuItem>
                        <MenuItem value="11">11 - November</MenuItem>
                        <MenuItem value="12">12 - Dezember</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel>Saison Bis</InputLabel>
                      <Select
                        value={editFormData.newKennzeichenSaisonBis || ''}
                        onChange={(e) => handleInputChange('newKennzeichenSaisonBis', e.target.value)}
                        label="Saison Bis"
                      >
                        <MenuItem value="">Keine</MenuItem>
                        <MenuItem value="01">01 - Januar</MenuItem>
                        <MenuItem value="02">02 - Februar</MenuItem>
                        <MenuItem value="03">03 - März</MenuItem>
                        <MenuItem value="04">04 - April</MenuItem>
                        <MenuItem value="05">05 - Mai</MenuItem>
                        <MenuItem value="06">06 - Juni</MenuItem>
                        <MenuItem value="07">07 - Juli</MenuItem>
                        <MenuItem value="08">08 - August</MenuItem>
                        <MenuItem value="09">09 - September</MenuItem>
                        <MenuItem value="10">10 - Oktober</MenuItem>
                        <MenuItem value="11">11 - November</MenuItem>
                        <MenuItem value="12">12 - Dezember</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!editFormData.newKennzeichenReserviert}
                          onChange={(e) => handleInputChange('newKennzeichenReserviert', e.target.checked)}
                        />
                      }
                      label="Kennzeichen reserviert"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Reservierungs-PIN"
                      value={editFormData.newKennzeichenReservierungPin || ''}
                      onChange={(e) => handleInputChange('newKennzeichenReservierungPin', e.target.value)}
                      autoComplete="off"
                      disabled={!editFormData.newKennzeichenReserviert}
                      helperText={!editFormData.newKennzeichenReserviert ? "Nur verfügbar wenn Kennzeichen reserviert ist" : ""}
                    />
                  </Grid>
                </>
              )}
              {/* Altes Kennzeichen - nur wenn erforderlich */}
              {fieldVisibility.altesKennzeichen && antrag && (antrag.geschaeftsvorfallTyp === 'AUSSERBETRIEBSETZUNG' || antrag.geschaeftsvorfallTyp === 'WIEDERZULASSUNG' || antrag.geschaeftsvorfallTyp === 'WIEDERZULASSUNG_SELBER_HALTER' || antrag.geschaeftsvorfallTyp === 'UMSCHREIBUNG_ANDERER_BEZIRK_OHNE_HALTERWECHSEL' || antrag.geschaeftsvorfallTyp === 'UMSCHREIBUNG_ANDERER_BEZIRK_MIT_HALTERWECHSEL' || antrag.geschaeftsvorfallTyp === 'UMSCHREIBUNG_INNERHALB_MIT_HALTERWECHSEL' || antrag.geschaeftsvorfallTyp === 'AENDERUNG_HALTERDATEN') ? (
                <>
                  {/* Altes Kennzeichen für AUSSERBETRIEBSETZUNG und WIEDERZULASSUNG - einmal eingeben, zwei Siegel-Codes */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Altes Kennzeichen</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!editFormData.hasOldKennzeichen}
                          onChange={(e) => {
                            handleInputChange('hasOldKennzeichen', e.target.checked);
                            handleInputChange('hasOldKennzeichenVorn', e.target.checked);
                            handleInputChange('hasOldKennzeichenHinten', e.target.checked);
                          }}
                        />
                      }
                      label="Altes Kennzeichen vorhanden"
                    />
                  </Grid>
                  {/* Vorn/Hinten-Auswahl – nur bei Außerbetriebsetzung */}
                  {editFormData.hasOldKennzeichen && antrag.geschaeftsvorfallTyp === 'AUSSERBETRIEBSETZUNG' && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2, ml: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!editFormData.hasOldKennzeichenVorn}
                              disabled={!editFormData.hasOldKennzeichenHinten}
                              onChange={(e) => {
                                handleInputChange('hasOldKennzeichenVorn', e.target.checked);
                                if (!e.target.checked && !editFormData.hasOldKennzeichenHinten) {
                                  handleInputChange('hasOldKennzeichen', false);
                                }
                              }}
                            />
                          }
                          label="Vorne"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!editFormData.hasOldKennzeichenHinten}
                              disabled={!editFormData.hasOldKennzeichenVorn}
                              onChange={(e) => {
                                handleInputChange('hasOldKennzeichenHinten', e.target.checked);
                                if (!e.target.checked && !editFormData.hasOldKennzeichenVorn) {
                                  handleInputChange('hasOldKennzeichen', false);
                                }
                              }}
                            />
                          }
                          label="Hinten"
                        />
                      </Box>
                    </Grid>
                  )}
                  {editFormData.hasOldKennzeichen && (
                    <>
                      <Grid item xs={3}>
                        <TextField
                          fullWidth
                          label="Bezirk"
                          value={editFormData.oldKennzeichenBezirk || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            const filtered = value.replace(/[^A-Za-zÄÖÜäöüß]/g, '').toUpperCase().slice(0, 3);
                            handleInputChange('oldKennzeichenBezirk', filtered);
                            handleInputChange('oldKennzeichenVornBezirk', filtered);
                            handleInputChange('oldKennzeichenHintenBezirk', filtered);
                          }}
                          onPaste={(e) => {
                            const parsed = parseKennzeichen(e.clipboardData.getData('text'));
                            if (parsed) {
                              e.preventDefault();
                              ['oldKennzeichen', 'oldKennzeichenVorn', 'oldKennzeichenHinten'].forEach(pfx => {
                                handleInputChange(`${pfx}Bezirk`, parsed.bezirk);
                                handleInputChange(`${pfx}Buchstaben`, parsed.buchstaben);
                                handleInputChange(`${pfx}Nummer`, parsed.nummer);
                              });
                            }
                          }}
                          inputProps={{ maxLength: 3 }}
                          autoComplete="off"
                          helperText="1-3 Buchstaben"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          fullWidth
                          label="Buchstaben"
                          value={editFormData.oldKennzeichenBuchstaben || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            const filtered = value.replace(/[^A-Za-zÄÖÜäöüß]/g, '').toUpperCase().slice(0, 3);
                            handleInputChange('oldKennzeichenBuchstaben', filtered);
                            handleInputChange('oldKennzeichenVornBuchstaben', filtered);
                            handleInputChange('oldKennzeichenHintenBuchstaben', filtered);
                          }}
                          onPaste={(e) => {
                            const parsed = parseKennzeichen(e.clipboardData.getData('text'));
                            if (parsed) {
                              e.preventDefault();
                              ['oldKennzeichen', 'oldKennzeichenVorn', 'oldKennzeichenHinten'].forEach(pfx => {
                                handleInputChange(`${pfx}Bezirk`, parsed.bezirk);
                                handleInputChange(`${pfx}Buchstaben`, parsed.buchstaben);
                                handleInputChange(`${pfx}Nummer`, parsed.nummer);
                              });
                            }
                          }}
                          inputProps={{ maxLength: 3 }}
                          autoComplete="off"
                          helperText="1-3 Buchstaben"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          fullWidth
                          label="Nummer"
                          value={editFormData.oldKennzeichenNummer || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            const filtered = value.replace(/[^0-9]/g, '').slice(0, 4);
                            handleInputChange('oldKennzeichenNummer', filtered);
                            handleInputChange('oldKennzeichenVornNummer', filtered);
                            handleInputChange('oldKennzeichenHintenNummer', filtered);
                          }}
                          onPaste={(e) => {
                            const parsed = parseKennzeichen(e.clipboardData.getData('text'));
                            if (parsed) {
                              e.preventDefault();
                              ['oldKennzeichen', 'oldKennzeichenVorn', 'oldKennzeichenHinten'].forEach(pfx => {
                                handleInputChange(`${pfx}Bezirk`, parsed.bezirk);
                                handleInputChange(`${pfx}Buchstaben`, parsed.buchstaben);
                                handleInputChange(`${pfx}Nummer`, parsed.nummer);
                              });
                            }
                          }}
                          inputProps={{ maxLength: 4 }}
                          autoComplete="off"
                          helperText="1-4 Ziffern"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel>Sonderkennzeichen</InputLabel>
                          <Select
                            value={editFormData.oldKennzeichenSonderkennzeichen || ''}
                            onChange={(e) => {
                              handleInputChange('oldKennzeichenSonderkennzeichen', e.target.value);
                              // Synchronisiere für Vorn und Hinten
                              handleInputChange('oldKennzeichenVornSonderkennzeichen', e.target.value);
                              handleInputChange('oldKennzeichenHintenSonderkennzeichen', e.target.value);
                            }}
                            label="Sonderkennzeichen"
                          >
                            <MenuItem value="">Kein</MenuItem>
                            <MenuItem value="E">E (Elektro)</MenuItem>
                            <MenuItem value="H">H (Historisch)</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {/* Siegel-Codes nur bei Außerbetriebsetzung */}
                      {antrag.geschaeftsvorfallTyp === 'AUSSERBETRIEBSETZUNG' && (
                        <>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                              Siegel-Sicherheitscodes
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Siegel-Code Vorn"
                              value={editFormData.oldKennzeichenVornSiegelSicherheitscode || ''}
                              disabled={!editFormData.hasOldKennzeichenVorn}
                              onChange={(e) => {
                                const value = e.target.value;
                                const filtered = value.slice(0, 3);
                                handleInputChange('oldKennzeichenVornSiegelSicherheitscode', filtered);
                              }}
                              inputProps={{ maxLength: 3 }}
                              autoComplete="off"
                              helperText={editFormData.hasOldKennzeichenVorn ? '3 Zeichen' : 'Position nicht ausgewählt'}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Siegel-Code Hinten"
                              value={editFormData.oldKennzeichenHintenSiegelSicherheitscode || ''}
                              disabled={!editFormData.hasOldKennzeichenHinten}
                              onChange={(e) => {
                                const value = e.target.value;
                                const filtered = value.slice(0, 3);
                                handleInputChange('oldKennzeichenHintenSiegelSicherheitscode', filtered);
                              }}
                              inputProps={{ maxLength: 3 }}
                              autoComplete="off"
                              helperText={editFormData.hasOldKennzeichenHinten ? '3 Zeichen' : 'Position nicht ausgewählt'}
                            />
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                  {/* Fahrzeugbezogene Kennzeichenreservierung - nur bei Außerbetriebsetzung */}
                  {antrag.geschaeftsvorfallTyp === 'AUSSERBETRIEBSETZUNG' && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                          Fahrzeugbezogene Kennzeichenreservierung
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!editFormData.kennzeichenReservierung}
                              onChange={(e) => handleInputChange('kennzeichenReservierung', e.target.checked)}
                            />
                          }
                          label="Kennzeichen für Wiederzulassung reservieren (max. 12 Monate)"
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 4 }}>
                          Das Kennzeichen wird für das Fahrzeug reserviert und kann im vorgegebenen Zeitraum nicht für andere Fahrzeuge genutzt werden.
                        </Typography>
                      </Grid>
                    </>
                  )}
                </>
              ) : fieldVisibility.altesKennzeichen ? (
                <>
                  {/* Single old license plate for other business process types */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!editFormData.hasOldKennzeichen}
                          onChange={(e) => handleInputChange('hasOldKennzeichen', e.target.checked)}
                        />
                      }
                      label="Altes Kennzeichen vorhanden"
                    />
                  </Grid>
                  {editFormData.hasOldKennzeichen && (
                    <>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Bezirk"
                          value={editFormData.oldKennzeichenBezirk || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            const filtered = value.replace(/[^A-Za-zÄÖÜäöüß]/g, '').toUpperCase().slice(0, 3);
                            handleInputChange('oldKennzeichenBezirk', filtered);
                          }}
                          onPaste={(e) => {
                            const parsed = parseKennzeichen(e.clipboardData.getData('text'));
                            if (parsed) {
                              e.preventDefault();
                              handleInputChange('oldKennzeichenBezirk', parsed.bezirk);
                              handleInputChange('oldKennzeichenBuchstaben', parsed.buchstaben);
                              handleInputChange('oldKennzeichenNummer', parsed.nummer);
                            }
                          }}
                          inputProps={{ maxLength: 3 }}
                          autoComplete="off"
                          helperText="1-3 Buchstaben"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Buchstaben"
                          value={editFormData.oldKennzeichenBuchstaben || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            const filtered = value.replace(/[^A-Za-zÄÖÜäöüß]/g, '').toUpperCase().slice(0, 3);
                            handleInputChange('oldKennzeichenBuchstaben', filtered);
                          }}
                          onPaste={(e) => {
                            const parsed = parseKennzeichen(e.clipboardData.getData('text'));
                            if (parsed) {
                              e.preventDefault();
                              handleInputChange('oldKennzeichenBezirk', parsed.bezirk);
                              handleInputChange('oldKennzeichenBuchstaben', parsed.buchstaben);
                              handleInputChange('oldKennzeichenNummer', parsed.nummer);
                            }
                          }}
                          inputProps={{ maxLength: 3 }}
                          autoComplete="off"
                          helperText="1-3 Buchstaben"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Nummer"
                          value={editFormData.oldKennzeichenNummer || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            const filtered = value.replace(/[^0-9]/g, '').slice(0, 4);
                            handleInputChange('oldKennzeichenNummer', filtered);
                          }}
                          onPaste={(e) => {
                            const parsed = parseKennzeichen(e.clipboardData.getData('text'));
                            if (parsed) {
                              e.preventDefault();
                              handleInputChange('oldKennzeichenBezirk', parsed.bezirk);
                              handleInputChange('oldKennzeichenBuchstaben', parsed.buchstaben);
                              handleInputChange('oldKennzeichenNummer', parsed.nummer);
                            }
                          }}
                          inputProps={{ maxLength: 4 }}
                          autoComplete="off"
                          helperText="1-4 Ziffern"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl fullWidth>
                          <InputLabel>Sonderkennzeichen</InputLabel>
                          <Select
                            value={editFormData.oldKennzeichenSonderkennzeichen || ''}
                            onChange={(e) => handleInputChange('oldKennzeichenSonderkennzeichen', e.target.value)}
                            label="Sonderkennzeichen"
                          >
                            <MenuItem value="">Kein</MenuItem>
                            <MenuItem value="E">E (Elektro)</MenuItem>
                            <MenuItem value="H">H (Historisch)</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  )}
                </>
              ) : null}
            </Grid>
          );

        case 'versandadresse':
          return (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Vorname"
                  value={editFormData.versandVorname || ''}
                  onChange={(e) => handleInputChange('versandVorname', e.target.value)}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={editFormData.versandName || ''}
                  onChange={(e) => handleInputChange('versandName', e.target.value)}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  label="Straße"
                  value={editFormData.versandStrasse || ''}
                  onChange={(e) => handleInputChange('versandStrasse', e.target.value)}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Hausnummer"
                  value={editFormData.versandHausnummer || ''}
                  onChange={(e) => handleInputChange('versandHausnummer', e.target.value)}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="PLZ"
                  value={editFormData.versandPostleitzahl || ''}
                  onChange={(e) => handleInputChange('versandPostleitzahl', e.target.value)}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  label="Ort"
                  value={editFormData.versandOrt || ''}
                  onChange={(e) => handleInputChange('versandOrt', e.target.value)}
                  autoComplete="off"
                />
              </Grid>
            </Grid>
          );

        case 'halterdaten':
          return (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Persontyp</InputLabel>
                  <Select
                    value={editFormData.personTyp || ''}
                    onChange={(e) => handleInputChange('personTyp', e.target.value)}
                    label="Persontyp"
                  >
                    <MenuItem value="">Nicht angegeben</MenuItem>
                    <MenuItem value={PersonTyp.NATUERLICHE_PERSON}>Natürliche Person</MenuItem>
                    <MenuItem value={PersonTyp.JURISTISCHE_PERSON}>Juristische Person</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {editFormData.personTyp === PersonTyp.NATUERLICHE_PERSON && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Vorname"
                      value={editFormData.vorname || ''}
                      onChange={(e) => handleInputChange('vorname', e.target.value)}
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Nachname"
                      value={editFormData.nachname || ''}
                      onChange={(e) => handleInputChange('nachname', e.target.value)}
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel>Geschlecht</InputLabel>
                      <Select
                        value={editFormData.geschlecht || ''}
                        onChange={(e) => handleInputChange('geschlecht', e.target.value)}
                        label="Geschlecht"
                      >
                        <MenuItem value="">Nicht angegeben</MenuItem>
                        <MenuItem value={Geschlecht.MAENNLICH}>Männlich</MenuItem>
                        <MenuItem value={Geschlecht.WEIBLICH}>Weiblich</MenuItem>
                        <MenuItem value={Geschlecht.DIVERS}>Divers</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Geburtsdatum"
                      type="date"
                      value={editFormData.geburtsdatum || ''}
                      onChange={(e) => handleInputChange('geburtsdatum', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Geburtsort"
                      value={editFormData.geburtsort || ''}
                      onChange={(e) => handleInputChange('geburtsort', e.target.value)}
                      autoComplete="off"
                    />
                  </Grid>
                </>
              )}
              {editFormData.personTyp === PersonTyp.JURISTISCHE_PERSON && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Firmenname"
                    value={editFormData.firma || ''}
                    onChange={(e) => handleInputChange('firma', e.target.value)}
                    autoComplete="off"
                  />
                </Grid>
              )}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Straße"
                  value={editFormData.strasse || ''}
                  onChange={(e) => handleInputChange('strasse', e.target.value)}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Hausnummer"
                  value={editFormData.hausnummer || ''}
                  onChange={(e) => handleInputChange('hausnummer', e.target.value)}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Hausnummerzusatz"
                  value={editFormData.adresszusatz || ''}
                  onChange={(e) => handleInputChange('adresszusatz', e.target.value)}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="PLZ"
                  value={editFormData.plz || ''}
                  onChange={(e) => handleInputChange('plz', e.target.value)}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  label="Ort"
                  value={editFormData.ort || ''}
                  onChange={(e) => handleInputChange('ort', e.target.value)}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Land"
                  value="Deutschland (D)"
                  InputProps={{ readOnly: true }}
                  helperText="Nur Halter mit Wohnsitz in Deutschland werden unterstützt"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="E-Mail"
                  type="email"
                  value={editFormData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12}>
                {(() => {
                  const ibanVal = (editFormData.iban || '').replace(/\s/g, '').toUpperCase();
                  const isValid = !ibanVal || (/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(ibanVal) && ibanVal.length >= 15 && ibanVal.length <= 34);
                  const displayVal = ibanVal.match(/.{1,4}/g)?.join(' ') || ibanVal;
                  return (
                    <TextField
                      fullWidth
                      label="IBAN"
                      value={displayVal}
                      onChange={(e) => handleInputChange('iban', e.target.value.replace(/\s/g, '').toUpperCase())}
                      autoComplete="off"
                      error={ibanVal.length > 0 && !isValid}
                      helperText={ibanVal.length > 0 && !isValid ? "Ungültige IBAN (Format: DE89 3704 0044 0532 0130 00)" : "IBAN wird automatisch formatiert (gespeichert ohne Leerzeichen)"}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                    />
                  );
                })()}
              </Grid>
            </Grid>
          );

        case 'verwertungsnachweis':
          // Validierung: Merkmal "0" erfordert Betriebsnummer
          const isBetriebsnummerRequired = editFormData.merkmalVerwertungsnachweis === '0';
          const isBetriebsnummerMissing = isBetriebsnummerRequired && !editFormData.betriebsnummerDemontagebetrieb;
          // Validierung: Merkmal "2" (Ausland) ist bei Deutschland nicht erlaubt
          const isAuslandMerkmalInvalid = editFormData.staatVerwertung === 'D' && editFormData.merkmalVerwertungsnachweis === '2';
          
          return (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Daten des Verwertungsnachweises für die Außerbetriebsetzung mit Fahrzeugverwertung.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Betriebsnummer Demontagebetrieb"
                  value={editFormData.betriebsnummerDemontagebetrieb || ''}
                  onChange={(e) => {
                    // Nur Ziffern erlauben
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    handleInputChange('betriebsnummerDemontagebetrieb', value);
                  }}
                  autoComplete="off"
                  required={isBetriebsnummerRequired}
                  error={isBetriebsnummerMissing}
                  helperText={isBetriebsnummerMissing 
                    ? "Betriebsnummer ist erforderlich wenn Verwertungsnachweis vorlag" 
                    : "Betriebsnummer des zertifizierten Demontagebetriebs"}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Ausstellungsdatum"
                  type="date"
                  value={editFormData.datumAusstellung || ''}
                  onChange={(e) => handleInputChange('datumAusstellung', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  autoComplete="off"
                  helperText="Datum der Ausstellung des Verwertungsnachweises"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Staat der Verwertung</InputLabel>
                  <Select
                    value={editFormData.staatVerwertung || 'D'}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value !== '__MORE__') {
                        handleInputChange('staatVerwertung', value);
                      }
                    }}
                    label="Staat der Verwertung"
                  >
                    {/* EU-Länder (ISO 3166-1 Alpha-2, außer D für Deutschland) */}
                    <MenuItem value="D">Deutschland (D)</MenuItem>
                    <MenuItem value="AT">Österreich (AT)</MenuItem>
                    <MenuItem value="BE">Belgien (BE)</MenuItem>
                    <MenuItem value="BG">Bulgarien (BG)</MenuItem>
                    <MenuItem value="HR">Kroatien (HR)</MenuItem>
                    <MenuItem value="CY">Zypern (CY)</MenuItem>
                    <MenuItem value="CZ">Tschechien (CZ)</MenuItem>
                    <MenuItem value="DK">Dänemark (DK)</MenuItem>
                    <MenuItem value="EE">Estland (EE)</MenuItem>
                    <MenuItem value="FI">Finnland (FI)</MenuItem>
                    <MenuItem value="FR">Frankreich (FR)</MenuItem>
                    <MenuItem value="GR">Griechenland (GR)</MenuItem>
                    <MenuItem value="HU">Ungarn (HU)</MenuItem>
                    <MenuItem value="IE">Irland (IE)</MenuItem>
                    <MenuItem value="IT">Italien (IT)</MenuItem>
                    <MenuItem value="LV">Lettland (LV)</MenuItem>
                    <MenuItem value="LT">Litauen (LT)</MenuItem>
                    <MenuItem value="LU">Luxemburg (LU)</MenuItem>
                    <MenuItem value="MT">Malta (MT)</MenuItem>
                    <MenuItem value="NL">Niederlande (NL)</MenuItem>
                    <MenuItem value="PL">Polen (PL)</MenuItem>
                    <MenuItem value="PT">Portugal (PT)</MenuItem>
                    <MenuItem value="RO">Rumänien (RO)</MenuItem>
                    <MenuItem value="SK">Slowakei (SK)</MenuItem>
                    <MenuItem value="SI">Slowenien (SI)</MenuItem>
                    <MenuItem value="ES">Spanien (ES)</MenuItem>
                    <MenuItem value="SE">Schweden (SE)</MenuItem>
                    {showAllCountries ? (
                      <>
                        {/* Weitere Länder (ISO 3166-1 Alpha-2) */}
                        <MenuItem disabled sx={{ fontWeight: 'bold', opacity: 1 }}>── Weitere Länder ──</MenuItem>
                        <MenuItem value="AL">Albanien (AL)</MenuItem>
                        <MenuItem value="AD">Andorra (AD)</MenuItem>
                        <MenuItem value="AM">Armenien (AM)</MenuItem>
                        <MenuItem value="AU">Australien (AU)</MenuItem>
                        <MenuItem value="AZ">Aserbaidschan (AZ)</MenuItem>
                        <MenuItem value="BA">Bosnien und Herzegowina (BA)</MenuItem>
                        <MenuItem value="BR">Brasilien (BR)</MenuItem>
                        <MenuItem value="BY">Belarus (BY)</MenuItem>
                        <MenuItem value="CA">Kanada (CA)</MenuItem>
                        <MenuItem value="CH">Schweiz (CH)</MenuItem>
                        <MenuItem value="CN">China (CN)</MenuItem>
                        <MenuItem value="GB">Großbritannien (GB)</MenuItem>
                        <MenuItem value="GE">Georgien (GE)</MenuItem>
                        <MenuItem value="IN">Indien (IN)</MenuItem>
                        <MenuItem value="IS">Island (IS)</MenuItem>
                        <MenuItem value="JP">Japan (JP)</MenuItem>
                        <MenuItem value="XK">Kosovo (XK)</MenuItem>
                        <MenuItem value="MD">Moldawien (MD)</MenuItem>
                        <MenuItem value="MX">Mexiko (MX)</MenuItem>
                        <MenuItem value="MK">Nordmazedonien (MK)</MenuItem>
                        <MenuItem value="ME">Montenegro (ME)</MenuItem>
                        <MenuItem value="NO">Norwegen (NO)</MenuItem>
                        <MenuItem value="NZ">Neuseeland (NZ)</MenuItem>
                        <MenuItem value="AR">Argentinien (AR)</MenuItem>
                        <MenuItem value="KR">Südkorea (KR)</MenuItem>
                        <MenuItem value="RU">Russland (RU)</MenuItem>
                        <MenuItem value="RS">Serbien (RS)</MenuItem>
                        <MenuItem value="TR">Türkei (TR)</MenuItem>
                        <MenuItem value="UA">Ukraine (UA)</MenuItem>
                        <MenuItem value="US">USA (US)</MenuItem>
                        <MenuItem value="ZA">Südafrika (ZA)</MenuItem>
                      </>
                    ) : (
                      <MenuItem 
                        value="__MORE__" 
                        sx={{ fontStyle: 'italic', color: 'primary.main' }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowAllCountries(true);
                        }}
                      >
                        Weitere Länder anzeigen...
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth error={isAuslandMerkmalInvalid}>
                  <InputLabel>Merkmal Verwertungsnachweis</InputLabel>
                  <Select
                    value={editFormData.merkmalVerwertungsnachweis || ''}
                    onChange={(e) => handleInputChange('merkmalVerwertungsnachweis', e.target.value)}
                    label="Merkmal Verwertungsnachweis"
                    error={isAuslandMerkmalInvalid}
                  >
                    <MenuItem value="">Nicht angegeben</MenuItem>
                    <MenuItem value="0">0 - Verwertungsnachweis lag vor</MenuItem>
                    <MenuItem value="1">1 - Das Fahrzeug wurde nicht als Abfall entsorgt</MenuItem>
                    <MenuItem 
                      value="2" 
                      disabled={editFormData.staatVerwertung === 'D'}
                    >
                      2 - Das Fahrzeug wurde zur Verwertung ins Ausland verbracht
                    </MenuItem>
                  </Select>
                  {isAuslandMerkmalInvalid && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      Bei Staat "Deutschland" ist "Verwertung ins Ausland" nicht zulässig
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              {(isBetriebsnummerMissing || isAuslandMerkmalInvalid) && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    Bitte korrigieren Sie die markierten Felder vor dem Speichern.
                  </Typography>
                </Grid>
              )}
            </Grid>
          );

        default:
          return null;
      }
    };

    return (
      <Dialog 
        open={true} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {renderDialogContent()}
          </Box>
        </DialogContent>
        <DialogActions>
          {openDialog === 'grunddaten' ? (
            // Nur Close-Button für Grunddaten (nur Anzeige, kein Speichern)
            <Button onClick={handleCloseDialog} variant="contained">
              Schließen
            </Button>
          ) : (
            // Abbrechen + Speichern für alle anderen Dialoge
            <>
              <Button onClick={handleCloseDialog}>
                Abbrechen
              </Button>
              <Button 
                onClick={handleSaveChanges}
                variant="contained"
                disabled={saving}
              >
                {saving ? 'Speichern...' : 'Speichern'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  }
};

export default ZulassungsantragDetailPage;
